import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import moment from 'moment';
import {
  MantineProvider,
  Progress,
  Group,
  Loader,
  Switch,
} from '@mantine/core';
import './App.css';
import { Task } from './components/Task';
import { DeleteModal } from './components/DeleteModal';
import { CategorySelect } from './components/CategorySelect';
import { CategoryModal } from './components/CategoryModal';
import { SnoozedTasksModal } from './components/SnoozedTasksModal';
import { UserSettingsModal } from './components/UserSettingsModal';
import { TbSearch, TbPin, TbClockDown, TbX } from 'react-icons/tb';
import SwitchSelector from 'react-switch-selector';
import { Login } from './components/Login';
import { db, auth } from './firebase';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
  writeBatch,
  getDoc,
  setDoc,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';
import { TaskDescriptionModal } from './components/TaskDescriptionModal';
// TaskHeatmap moved into ActivityPanel
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
import { ComparisonModal } from './components/ComparisonModal';
import { Sidebar } from './components/Sidebar';
import { NotepadPanel } from './components/NotepadPanel';
import { SettingsPanel } from './components/SettingsPanel';
import { ActivityPanel } from './components/ActivityPanel';
import { PeoplePanel } from './components/PeoplePanel';
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
import { PriorityBandSelector } from './components/PriorityBandSelector';
import { aggregatePeopleFromTasks, PersonWithCount } from './utils/peopleUtils';
import {
  UrgencyBand,
  ComparisonSession,
  createComparisonSession,
  processChoice,
  undoLastChoice,
  isSessionConverged,
  getFinalRank,
  getBandFromNumber,
  deriveUrgencyNumber,
  getBandMidpoint,
  getBandMax,
  getPivotIndex,
} from './utils/stackRanking';
import {
  PreferenceGraph,
  buildPreferenceGraph,
  computeTransitiveClosure,
  addComparison as addComparisonToGraph,
  findKnownPosition,
  filterToUnknownCandidates,
} from './utils/preferenceGraph';
import {
  SmartRankingState,
  getNextSmartComparison,
  processSmartChoice,
  processSmartTooTough,
  processSmartSkip,
  getSmartRankingProgress,
} from './utils/smartRanking';
import { BucketingModal } from './components/BucketingModal';
import { TournamentModal } from './components/TournamentModal';
import { ComparisonScopeModal } from './components/ComparisonScopeModal';
import {
  BucketingState,
  TournamentState,
  RerankState,
  initializeBucketing,
  isBucketingComplete,
  serializeRerankState,
  deserializeRerankState,
} from './utils/bucketingState';
import { initializeTournament, isTournamentComplete } from './utils/tournamentRanking';

export interface SharedUser {
  email: string;
  userId: string;
  photoURL?: string;
}

export interface SharedBy {
  ownerEmail: string;
  ownerUserId: string;
  originalTaskId: string;
}

export interface TaskUpdates {
  title?: string;
  description?: string;
  urgency?: 'high' | 'medium' | 'low' | null;
  urgencyNumber?: number;
  urgencyBand?: 'high' | 'medium' | 'low' | null;
  urgencyRank?: number | null;
  hasCustomPriority?: boolean;
  completed?: boolean;
  completedAt?: number | null;
  timerStartedAt?: number | ReturnType<typeof deleteField>;
  totalElapsedTime?: number;
  userTimeTracking?: { [userId: string]: number };
}

export interface TaskItem {
  id: string;
  title: string;
  completed: boolean;
  completedAt?: number;
  categoryId: string;
  urgency: 'high' | 'medium' | 'low' | null; // Keep for backward compat
  urgencyNumber?: number; // Keep for backward compat, will be derived
  urgencyBand?: 'high' | 'medium' | 'low' | null; // New: the band
  urgencyRank?: number | null; // New: position within band (1 = highest)
  hasCustomPriority?: boolean; // True when user set priority via slider
  snoozedUntil?: number;
  createdAt: number;
  timezone: string;
  willDoToday: 'today' | 'pinned' | false;
  description?: string;
  manualOrder?: number; // Manual ordering within category
  source?: {
    type: string;
    url?: string;
    channelId?: string;
    messageId?: string;
  };
  timerStartedAt?: number; // Timestamp when timer started
  totalElapsedTime?: number; // Total elapsed time in seconds (for current user)
  userTimeTracking?: { [userId: string]: number }; // Time spent per user in seconds
  sharedWith?: SharedUser[]; // Users this task is shared with
  sharedBy?: SharedBy; // If this is a shared task, who shared it
  moreUrgentThan?: string[]; // Task IDs this task is more urgent than (comparison graph)
  recurrenceDays?: number; // Interval in days for recurrence (undefined = not recurring)
  completionLog?: number[]; // Array of completion timestamps for recurring tasks
}

interface Category {
  id: string;
  name: string;
  order: number;
  archived?: boolean;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [allTasksForHeatmap, setAllTasksForHeatmap] = useState<TaskItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [archivedCategories, setArchivedCategories] = useState<Category[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [isPinnedFilterActive, setIsPinnedFilterActive] = useState(false);
  const [isTodayFilterActive, setIsTodayFilterActive] = useState(false);
  const [isCompletedFilterActive, setIsCompletedFilterActive] = useState(false);
  const [isTimerFilterActive, setIsTimerFilterActive] = useState(false);
  const [personFilter, setPersonFilter] = useState<string | null>(null);
  const [newTaskCategoryId, setNewTaskCategoryId] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [newTaskUrgencyNumber, setNewTaskUrgencyNumber] = useState(1); // Kept for backward compatibility with preferences
  const [newTaskWillDoToday, setNewTaskWillDoToday] = useState<
    'today' | 'pinned' | false
  >(false);
  const [newTaskSwitcherTooltip, setNewTaskSwitcherTooltip] = useState<string | null>(null);
  const [newTaskSwitcherTooltipPosition, setNewTaskSwitcherTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isFirebaseInitialized, setIsFirebaseInitialized] = useState(false);
  const [deleteModalTask, setDeleteModalTask] = useState<TaskItem | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSnoozedTasksModalOpen, setIsSnoozedTasksModalOpen] = useState(false);
  const [isUserSettingsModalOpen, setIsUserSettingsModalOpen] = useState(false);
  const [descriptionModalTask, setDescriptionModalTask] =
    useState<TaskItem | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [draggedCategory, setDraggedCategory] = useState<Category | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [tempCategories, setTempCategories] = useState<Category[]>([]);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [heatmapFilterDate, setHeatmapFilterDate] = useState<Date | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>(() => {
    // Initialize from localStorage or detect from browser
    const stored = localStorage.getItem('userTimezone');
    return stored || Intl.DateTimeFormat().resolvedOptions().timeZone;
  });
  const [lastTodayTaskCheck, setLastTodayTaskCheck] = useState<string | null>(
    null
  );
  const [todayExtendedUntil, setTodayExtendedUntil] = useState<string | null>(
    null
  );

  // Stack ranking comparison state
  const [comparisonSession, setComparisonSession] = useState<ComparisonSession | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [pendingNewTask, setPendingNewTask] = useState<TaskItem | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [comparisonNumber, setComparisonNumber] = useState(1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [newTaskBand, setNewTaskBand] = useState<UrgencyBand | null>(null);
  const [rerankQueue, setRerankQueue] = useState<string[]>([]);
  const [rerankAllTotal, setRerankAllTotal] = useState<number>(0);
  const [rerankAllCompleted, setRerankAllCompleted] = useState<number>(0);
  const [rerankAllComplete, setRerankAllComplete] = useState(false);
  const [preferenceGraph, setPreferenceGraph] = useState<PreferenceGraph | null>(null);
  const [comparisonsSkipped, setComparisonsSkipped] = useState<number>(0);
  const [smartRankingState, setSmartRankingState] = useState<SmartRankingState | null>(null);
  const [smartRankingCurrentPair, setSmartRankingCurrentPair] = useState<{ taskA: string; taskB: string } | null>(null);
  const [celebrationData, setCelebrationData] = useState<{
    score: number;
    band: UrgencyBand;
    rank: number;
    totalInBand: number;
    taskTitle: string;
    taskId?: string;
  } | null>(null);
  const handleFinalizeRankRef = useRef<((taskId: string, rank: number, band: UrgencyBand) => Promise<void>) | null>(null);
  const smartRankingLoadedRef = useRef(false);

  // Rerank flow state (bucketing + tournament)
  const [rerankState, setRerankState] = useState<RerankState | null>(null);
  const rerankStateLoadedRef = useRef(false);
  const [isBucketingModalOpen, setIsBucketingModalOpen] = useState(false);
  const [isTournamentModalOpen, setIsTournamentModalOpen] = useState(false);
  const [rerankingBucket, setRerankingBucket] = useState<'high' | 'medium' | 'low' | null>(null);

  // Comparison scope modal state (for category-filtered comparisons)
  const [isComparisonScopeModalOpen, setIsComparisonScopeModalOpen] = useState(false);
  const [pendingTaskForScopeChoice, setPendingTaskForScopeChoice] = useState<TaskItem | null>(null);
  const [pendingBandForScopeChoice, setPendingBandForScopeChoice] = useState<UrgencyBand | null>(null);

  // Sidebar & notepad state
  const [activeWidget, setActiveWidget] = useState<string | null>(null);
  const [notepadContent, setNotepadContent] = useState<string>('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 801);
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const stored = localStorage.getItem('panelWidth');
    return stored ? Math.max(305, parseInt(stored, 10)) : 305;
  });
  const [useModalForNotepad, setUseModalForNotepad] = useState(window.innerWidth < 801);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category');
  const pinnedParam = searchParams.get('pinned');
  const todayParam = searchParams.get('today');
  const completedParam = searchParams.get('completed');
  const timerParam = searchParams.get('timer');

  // Auto-resize textarea to fit content
  const adjustTextareaHeight = () => {
    if (inputRef.current) {
      const textarea = inputRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [newTaskTitle, searchQuery, isSearchMode]);

  // Focus input when entering search mode
  useEffect(() => {
    if (isSearchMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchMode]);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 801);
      setUseModalForNotepad(width < 801);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load notepad content from Firestore
  useEffect(() => {
    if (user) {
      const loadNotepad = async () => {
        const docRef = doc(db, 'users', user.uid, 'preferences', 'notepad');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setNotepadContent(snap.data().content || '');
        }
      };
      loadNotepad();
    }
  }, [user]);

  // Debounced save for notepad
  const saveNotepadRef = useRef<ReturnType<typeof setTimeout>>();
  const handleNotepadChange = useCallback((content: string) => {
    setNotepadContent(content);

    if (saveNotepadRef.current) {
      clearTimeout(saveNotepadRef.current);
    }

    saveNotepadRef.current = setTimeout(() => {
      if (user) {
        const docRef = doc(db, 'users', user.uid, 'preferences', 'notepad');
        setDoc(docRef, { content, updatedAt: serverTimestamp() }, { merge: true });
      }
    }, 500);
  }, [user]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (saveNotepadRef.current) {
        clearTimeout(saveNotepadRef.current);
      }
    };
  }, []);

  // Handle panel width change
  const handlePanelWidthChange = useCallback((width: number) => {
    // Min 305px, max leaves room for task-container (432px) + padding (100px) + sidebar (64px)
    const maxWidth = Math.max(305, window.innerWidth - 432 - 100 - 64);
    const clampedWidth = Math.max(305, Math.min(maxWidth, width));
    setPanelWidth(clampedWidth);
    localStorage.setItem('panelWidth', String(clampedWidth));
  }, []);

  // Load user preferences when user is authenticated
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!isFirebaseInitialized || !user) return;

      try {
        const userPrefsRef = doc(
          db,
          `users/${user.uid}/preferences`,
          'general'
        );
        const userPrefsDoc = await getDoc(userPrefsRef);

        if (userPrefsDoc.exists()) {
          const data = userPrefsDoc.data();
          if (data.newTaskUrgencyNumber !== undefined) {
            setNewTaskUrgencyNumber(data.newTaskUrgencyNumber);
          }
          if (data.lastTodayTaskCheck !== undefined) {
            setLastTodayTaskCheck(data.lastTodayTaskCheck);
          }
          if (data.todayExtendedUntil !== undefined) {
            setTodayExtendedUntil(data.todayExtendedUntil);
          }
          // Load persisted merge-sort state if exists (stored as JSON string)
          if (data.smartRankingStateJson) {
            try {
              const parsedState = JSON.parse(data.smartRankingStateJson) as SmartRankingState;
              if (!parsedState.isComplete) {
                setSmartRankingState(parsedState);
                setRerankAllTotal(parsedState.allTasks?.length || 0);
                setComparisonsSkipped(parsedState.comparisonsSkipped || 0);
              }
            } catch (e) {
              console.error('Error parsing merge-sort state:', e);
            }
          }
          if (data.smartRankingCurrentPair) {
            setSmartRankingCurrentPair(data.smartRankingCurrentPair);
          }
        }
        // Mark that we've loaded (even if no state was persisted)
        smartRankingLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading user preferences:', error);
        // Still mark as loaded so we don't block persistence
        smartRankingLoadedRef.current = true;
      }
    };

    loadUserPreferences();
  }, [isFirebaseInitialized, user]);

  // Persist merge-sort state to Firestore when it changes
  useEffect(() => {
    const persistMergeSortState = async () => {
      if (!isFirebaseInitialized || !user) return;

      try {
        const userPrefsRef = doc(db, `users/${user.uid}/preferences`, 'general');

        if (smartRankingState && !smartRankingState.isComplete) {
          // Save in-progress state (serialize to JSON to handle nested arrays)
          await setDoc(
            userPrefsRef,
            {
              smartRankingStateJson: JSON.stringify(smartRankingState),
              smartRankingCurrentPair: smartRankingCurrentPair,
            },
            { merge: true }
          );
        } else if ((smartRankingState === null || smartRankingState?.isComplete) && smartRankingLoadedRef.current) {
          // Clear persisted state when cancelled, null, or complete
          // Only clear if we've done the initial load (to avoid race condition on mount)
          await setDoc(
            userPrefsRef,
            {
              smartRankingStateJson: null,
              smartRankingCurrentPair: null,
            },
            { merge: true }
          );
        }
      } catch (error) {
        console.error('Error persisting merge-sort state:', error);
      }
    };

    persistMergeSortState();
  }, [isFirebaseInitialized, user, smartRankingState, smartRankingCurrentPair]);

  // Load persisted rerank state on startup
  useEffect(() => {
    const loadRerankState = async () => {
      if (!isFirebaseInitialized || !user) return;

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        const savedStateJson = userDoc.data()?.rerankStateJson;

        if (savedStateJson) {
          const savedState = deserializeRerankState(savedStateJson);
          if (savedState) {
            // Only restore if there's actual progress to resume
            const hasBucketingProgress = savedState.bucketingState && !isBucketingComplete(savedState.bucketingState);
            const hasTournamentProgress = savedState.tournamentState && !isTournamentComplete(savedState.tournamentState);
            const hasCompletedBucketing = savedState.bucketingState && isBucketingComplete(savedState.bucketingState) && !savedState.tournamentState;

            if (hasBucketingProgress || hasTournamentProgress || hasCompletedBucketing) {
              setRerankState(savedState);
            }
          }
        }
      } catch (error) {
        console.error('Error loading rerank state:', error);
      } finally {
        // Mark as loaded so save effect can run
        rerankStateLoadedRef.current = true;
      }
    };

    loadRerankState();
  }, [isFirebaseInitialized, user]);

  // Persist rerank state to Firestore
  useEffect(() => {
    // Don't save until we've attempted to load first (prevents clearing saved state on initial render)
    if (!isFirebaseInitialized || !user || !rerankStateLoadedRef.current) return;

    const saveState = async () => {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        if (rerankState) {
          await setDoc(userDocRef, {
            rerankStateJson: serializeRerankState(rerankState),
          }, { merge: true });
        } else {
          // Clear saved state when flow completes
          await setDoc(userDocRef, {
            rerankStateJson: null,
          }, { merge: true });
        }
      } catch (error) {
        console.error('Error saving rerank state:', error);
      }
    };

    saveState();
  }, [isFirebaseInitialized, user, rerankState]);

  // Update selected category and filters when URL changes
  useEffect(() => {
    // Update filters based on URL params
    setIsPinnedFilterActive(pinnedParam === 'true');
    setIsTodayFilterActive(todayParam === 'true');
    setIsCompletedFilterActive(completedParam === 'true');
    setIsTimerFilterActive(timerParam === 'true');

    // Update category
    if (categoryId) {
      setSelectedCategoryId(categoryId);
      // Set newTaskCategoryId if it's a regular category (not 'all')
      if (categoryId !== 'all') {
        setNewTaskCategoryId(categoryId);
      }
    } else {
      // Default to 'all' if no category is specified
      setSelectedCategoryId('all');
      navigate('?category=all', { replace: true });
    }
  }, [
    categoryId,
    pinnedParam,
    todayParam,
    completedParam,
    timerParam,
    navigate,
  ]);

  // Update newTaskWillDoToday when category pill filters change
  useEffect(() => {
    if (isPinnedFilterActive) {
      setNewTaskWillDoToday('pinned');
    } else if (isTodayFilterActive) {
      setNewTaskWillDoToday('today');
    } else {
      setNewTaskWillDoToday(false);
    }
  }, [isPinnedFilterActive, isTodayFilterActive]);

  // Check if Firebase is initialized
  useEffect(() => {
    if (db) {
      setIsFirebaseInitialized(true);
    }
  }, []);

  // Load tasks and categories
  useEffect(() => {
    const fetchData = async () => {
      if (!isFirebaseInitialized || !user) return;

      try {
        // Fetch categories
        const categoriesSnapshot = await getDocs(
          collection(db, `users/${user.uid}/categories`)
        );
        const loadedCategories = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          order: doc.data().order,
          archived: doc.data().archived || false,
        }));

        // Separate archived and non-archived categories
        const activeCategories = loadedCategories.filter(cat => !cat.archived);
        const archivedCategories = loadedCategories.filter(cat => cat.archived);

        // Sort categories by order
        const sortedActiveCategories = activeCategories.sort(
          (a, b) => a.order - b.order
        );
        const sortedArchivedCategories = archivedCategories.sort(
          (a, b) => a.order - b.order
        );

        setCategories(sortedActiveCategories);
        setArchivedCategories(sortedArchivedCategories);

        // Fetch tasks
        const tasksQuery = query(
          collection(db, `users/${user.uid}/tasks`),
          orderBy('completed'),
          orderBy('completedAt', 'desc')
        );
        const tasksSnapshot = await getDocs(tasksQuery);
        const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
        const threeMonthsAgo = Date.now() - 90 * 24 * 60 * 60 * 1000; // 90 days in milliseconds

        const allLoadedTasks = tasksSnapshot.docs.map(doc => {
          const data = doc.data();
          // Convert old boolean willDoToday to new three-state system
          let willDoToday: 'today' | 'pinned' | false = false;
          if (data.willDoToday === true) {
            willDoToday = 'pinned'; // Convert old true values to 'pinned'
          } else if (
            data.willDoToday === 'today' ||
            data.willDoToday === 'pinned'
          ) {
            willDoToday = data.willDoToday;
          }

          return {
            id: doc.id,
            title: data.title,
            completed: data.completed,
            completedAt: data.completedAt,
            categoryId: data.categoryId,
            urgency: data.urgency,
            urgencyNumber:
              data.urgencyNumber ||
              (data.urgency === 'low' ? 0 : data.urgency === 'medium' ? 4 : 8),
            urgencyBand: data.urgencyBand,
            urgencyRank: data.urgencyRank,
            hasCustomPriority: data.hasCustomPriority,
            snoozedUntil: data.snoozedUntil,
            createdAt: data.createdAt,
            timezone: data.timezone,
            willDoToday: willDoToday,
            description: data.description,
            manualOrder: data.manualOrder,
            timerStartedAt: data.timerStartedAt,
            totalElapsedTime: data.totalElapsedTime,
            userTimeTracking: data.userTimeTracking || {},
            source: data.source,
            sharedWith: data.sharedWith,
            sharedBy: data.sharedBy,
            moreUrgentThan: data.moreUrgentThan || [],
          } as TaskItem;
        });

        // For the heatmap, keep completed tasks from the last 3 months
        const tasksForHeatmap = allLoadedTasks.filter(
          task =>
            !task.completed ||
            (task.completedAt && task.completedAt > threeMonthsAgo)
        );
        setAllTasksForHeatmap(tasksForHeatmap);

        // For the main view, only keep tasks from the last week
        const loadedTasks = allLoadedTasks.filter(
          task =>
            !task.completed ||
            (task.completedAt && task.completedAt > oneWeekAgo)
        );

        setTasks(sortTasks(loadedTasks, sortedActiveCategories));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [isFirebaseInitialized, user]);

  // Handle expiration of 'today' tasks at midnight
  useEffect(() => {
    const checkAndExpireTodayTasks = async () => {
      if (!isFirebaseInitialized || !user || tasks.length === 0) return;

      // Get the current date string in the user's selected timezone (YYYY-MM-DD)
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const today = formatter.format(now); // Returns YYYY-MM-DD format

      // If we haven't checked today, it's a new day
      if (lastTodayTaskCheck && lastTodayTaskCheck !== today) {
        // Only expire tasks if "Don't expire tasks" is NOT active
        if (!todayExtendedUntil) {
          // Find all uncompleted tasks with willDoToday === 'today'
          const expiredTodayTasks = tasks.filter(
            task => task.willDoToday === 'today' && !task.completed
          );

          if (expiredTodayTasks.length > 0) {
            try {
              // Update all expired tasks to 'pinned' in Firebase
              const batch = writeBatch(db);
              expiredTodayTasks.forEach(task => {
                const taskRef = doc(db, `users/${user.uid}/tasks`, task.id);
                batch.update(taskRef, { willDoToday: 'pinned' });
              });
              await batch.commit();

              // Update local state
              setTasks(prevTasks =>
                prevTasks.map(task =>
                  expiredTodayTasks.find(t => t.id === task.id)
                    ? { ...task, willDoToday: 'pinned' as const }
                    : task
                )
              );
            } catch (error) {
              console.error('Error expiring today tasks:', error);
            }
          }
        }
      }

      // Update the last check date in state and database
      if (lastTodayTaskCheck !== today) {
        setLastTodayTaskCheck(today);
        try {
          const userPrefsRef = doc(
            db,
            `users/${user.uid}/preferences`,
            'general'
          );
          await setDoc(
            userPrefsRef,
            { lastTodayTaskCheck: today },
            { merge: true }
          );
        } catch (error) {
          console.error('Error saving lastTodayTaskCheck:', error);
        }
      }
    };

    // Check immediately when component mounts
    checkAndExpireTodayTasks().catch(error => {
      console.error('Error in initial today task check:', error);
    });

    // Set up an interval to check every minute
    const intervalId = setInterval(() => {
      checkAndExpireTodayTasks().catch(error => {
        console.error('Error in today task check interval:', error);
      });
    }, 60000);

    return () => clearInterval(intervalId);
  }, [
    isFirebaseInitialized,
    user,
    tasks,
    userTimezone,
    lastTodayTaskCheck,
    todayExtendedUntil,
  ]);

  const filterAllTasksInCategory = (currentTask: TaskItem): TaskItem[] => {
    // Find all tasks in the same section (completed status, category, urgency, will do today)
    return tasks.filter(
      task =>
        task.categoryId === currentTask.categoryId &&
        task.completed === currentTask.completed &&
        task.willDoToday === currentTask.willDoToday &&
        task.urgency === currentTask.urgency
    );
  };

  // Clean up comparison references when a task is deleted
  const cleanupComparisonReferences = async (deletedTaskId: string) => {
    if (!user) return;

    try {
      // Find all tasks that reference the deleted task in their moreUrgentThan
      const tasksWithReference = tasks.filter(
        t => t.moreUrgentThan?.includes(deletedTaskId)
      );

      if (tasksWithReference.length === 0) return;

      const batch = writeBatch(db);
      const tasksRef = collection(db, 'users', user.uid, 'tasks');

      for (const task of tasksWithReference) {
        const updatedRelations = (task.moreUrgentThan || []).filter(
          id => id !== deletedTaskId
        );
        batch.update(doc(tasksRef, task.id), {
          moreUrgentThan: updatedRelations,
        });
      }

      await batch.commit();

      // Update local state
      setTasks(prev =>
        prev.map(t => {
          if (t.moreUrgentThan?.includes(deletedTaskId)) {
            return {
              ...t,
              moreUrgentThan: (t.moreUrgentThan || []).filter(
                id => id !== deletedTaskId
              ),
            };
          }
          return t;
        })
      );
    } catch (error) {
      console.error('Error cleaning up comparison references:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!isFirebaseInitialized || !user) return;

    const task = tasks.find(t => t.id === id);
    if (!task) return;

    setDeleteModalTask(task);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalTask || !user) return;

    try {
      // If invitee is leaving a shared task
      if (deleteModalTask.sharedBy) {
        // Remove this user from the owner's sharedWith list
        await fetch('/api/remove-shared-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            taskId: deleteModalTask.sharedBy.originalTaskId,
            ownerUserId: deleteModalTask.sharedBy.ownerUserId,
            userIdToRemove: user.uid,
          }),
        });

        // Delete the task from invitee's account
        await deleteDoc(doc(db, `users/${user.uid}/tasks`, deleteModalTask.id));
      }
      // If the task is shared with others (owner deleting), use the API to delete all copies
      else if (
        deleteModalTask.sharedWith &&
        deleteModalTask.sharedWith.length > 0
      ) {
        const response = await fetch('/api/delete-shared-task', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            taskId: deleteModalTask.id,
            ownerUserId: user.uid,
          }),
        });

        const data = await response.json();

        if (!data.success) {
          console.error('Error deleting shared task:', data.error);
          // Fall back to local deletion
          await deleteDoc(
            doc(db, `users/${user.uid}/tasks`, deleteModalTask.id)
          );
        }
      } else {
        // Normal deletion (not shared)
        await deleteDoc(doc(db, `users/${user.uid}/tasks`, deleteModalTask.id));
      }

      // Clean up comparison references to this task
      await cleanupComparisonReferences(deleteModalTask.id);

      setTasks(tasks.filter(task => task.id !== deleteModalTask.id));
      setAllTasksForHeatmap(
        allTasksForHeatmap.filter(task => task.id !== deleteModalTask.id)
      );

      // Show temporary alert
      const alert = document.createElement('div');
      alert.className = 'delete-alert';
      alert.textContent = `Task "${deleteModalTask.title}" ${deleteModalTask.sharedBy ? 'left' : 'deleted'}`;
      document.body.appendChild(alert);

      // Remove alert after 2 seconds
      setTimeout(() => {
        alert.remove();
      }, 2000);

      setDeleteModalTask(null);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalTask(null);
  };

  const syncSharedTask = useCallback(async (taskId: string, updates: TaskUpdates) => {
    if (!user) return;

    // Only sync specific fields: title, description, completed, completedAt, createdAt, userTimeTracking
    const allowedFields: (keyof TaskUpdates)[] = [
      'title',
      'description',
      'completed',
      'completedAt',
      'userTimeTracking',
    ];
    const filteredUpdates: Partial<TaskUpdates> = {};

    for (const key of Object.keys(updates)) {
      if (allowedFields.includes(key as keyof TaskUpdates)) {
        const typedKey = key as keyof TaskUpdates;
        const value = updates[typedKey];
        if (value !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (filteredUpdates as Record<string, any>)[typedKey] = value;
        }
      }
    }

    // Only sync if there are fields to sync
    if (Object.keys(filteredUpdates).length === 0) {
      return;
    }

    try {
      await fetch('/api/sync-shared-task', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          taskId,
          userId: user.uid,
          updates: filteredUpdates,
        }),
      });
    } catch (error) {
      console.error('Error syncing shared task:', error);
    }
  }, [user]);

  const handleToggle = useCallback(async (id: string) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      const task = tasks.find(t => t.id === id);
      if (task) {
        const now = Date.now();
        const isCompleting = !task.completed;

        // Handle recurring tasks: snooze instead of complete
        if (isCompleting && task.recurrenceDays && task.recurrenceDays > 0) {
          const snoozedUntil = now + task.recurrenceDays * 24 * 60 * 60 * 1000;
          const newCompletionLog = [...(task.completionLog || []), now];

          // Stop timer if running
          let timerUpdates: {
            timerStartedAt?: undefined;
            totalElapsedTime?: number;
            userTimeTracking?: { [userId: string]: number };
          } = {};

          if (task.timerStartedAt) {
            const sessionElapsed = Math.floor((now - task.timerStartedAt) / 1000);
            const totalElapsed = (task.totalElapsedTime || 0) + sessionElapsed;
            const userTimeTracking = task.userTimeTracking || {};
            const currentUserTime = userTimeTracking[user.uid] || 0;
            timerUpdates = {
              timerStartedAt: undefined,
              totalElapsedTime: totalElapsed,
              userTimeTracking: { ...userTimeTracking, [user.uid]: currentUserTime + sessionElapsed },
            };
          }

          const updates = {
            snoozedUntil,
            completionLog: newCompletionLog,
            ...(task.timerStartedAt ? {
              timerStartedAt: deleteField(),
              totalElapsedTime: timerUpdates.totalElapsedTime,
              userTimeTracking: timerUpdates.userTimeTracking,
            } : {}),
          };

          await updateDoc(doc(db, `users/${user.uid}/tasks`, id), updates);

          const updatedTask = {
            ...task,
            snoozedUntil,
            completionLog: newCompletionLog,
            ...timerUpdates,
          };

          setTasks(tasks.map(t => (t.id === id ? updatedTask : t)));
          setAllTasksForHeatmap(allTasksForHeatmap.map(t => (t.id === id ? updatedTask : t)));

          if (descriptionModalTask && descriptionModalTask.id === id) {
            setDescriptionModalTask(updatedTask);
          }

          // Show alert for recurring task completion
          const alert = document.createElement('div');
          alert.className = 'snooze-alert';
          alert.textContent = `Recurring task completed! Snoozed for ${task.recurrenceDays} day${task.recurrenceDays > 1 ? 's' : ''}`;
          document.body.appendChild(alert);
          setTimeout(() => alert.remove(), 2000);

          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updates: any = {
          completed: isCompleting,
          completedAt: isCompleting ? now : null,
        };

        // If completing a task with an active timer, stop the timer
        let timerUpdates: {
          timerStartedAt?: undefined;
          totalElapsedTime?: number;
          userTimeTracking?: { [userId: string]: number };
        } = {};

        if (isCompleting && task.timerStartedAt) {
          const sessionElapsed = Math.floor(
            (now - task.timerStartedAt) / 1000
          );
          const totalElapsed = (task.totalElapsedTime || 0) + sessionElapsed;

          const userTimeTracking = task.userTimeTracking || {};
          const currentUserTime = userTimeTracking[user.uid] || 0;
          const updatedUserTimeTracking = {
            ...userTimeTracking,
            [user.uid]: currentUserTime + sessionElapsed,
          };

          updates.timerStartedAt = deleteField();
          updates.totalElapsedTime = totalElapsed;
          updates.userTimeTracking = updatedUserTimeTracking;

          timerUpdates = {
            timerStartedAt: undefined,
            totalElapsedTime: totalElapsed,
            userTimeTracking: updatedUserTimeTracking,
          };
        }

        await updateDoc(doc(db, `users/${user.uid}/tasks`, id), updates);

        // Sync if this is a shared task
        if (task.sharedWith?.length || task.sharedBy) {
          const syncUpdates: { completed: boolean; completedAt: number | null; userTimeTracking?: { [userId: string]: number } } = {
            completed: isCompleting,
            completedAt: isCompleting ? now : null,
          };
          if (timerUpdates.userTimeTracking) {
            syncUpdates.userTimeTracking = timerUpdates.userTimeTracking;
          }
          await syncSharedTask(id, syncUpdates);
        }

        // Update descriptionModalTask if it's the same task
        if (descriptionModalTask && descriptionModalTask.id === id) {
          setDescriptionModalTask({
            ...descriptionModalTask,
            completed: isCompleting,
            completedAt: isCompleting ? now : undefined,
            ...timerUpdates,
          });
        }
        const updatedTask = {
          ...task,
          completed: isCompleting,
          completedAt: isCompleting ? now : undefined,
          ...timerUpdates,
        };

        setTasks(tasks.map(t => (t.id === id ? updatedTask : t)));

        setAllTasksForHeatmap(
          allTasksForHeatmap.map(t => (t.id === id ? updatedTask : t))
        );
      }
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  }, [isFirebaseInitialized, user, tasks, descriptionModalTask, allTasksForHeatmap, syncSharedTask]);

  const handleEdit = async (id: string, newTitle: string) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      const task = tasks.find(t => t.id === id);
      const updates = { title: newTitle };

      await updateDoc(doc(db, `users/${user.uid}/tasks`, id), updates);

      // Sync if this is a shared task
      if (task && (task.sharedWith?.length || task.sharedBy)) {
        await syncSharedTask(id, updates);
      }

      setTasks(
        tasks.map(task =>
          task.id === id ? { ...task, title: newTitle } : task
        )
      );
    } catch (error) {
      console.error('Error editing task:', error);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFirebaseInitialized || !user) return;

    if (newTaskTitle.trim()) {
      setIsAddingTask(true);
      try {
        const now = Date.now();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // A task without an explicit band goes to the backlog with no comparison.
        // Today/Pinned filters only force a destination when the user has also
        // committed to a band — otherwise capture stays friction-free.
        const hasBand = newTaskBand !== null;
        const willDoToday: 'today' | 'pinned' | false = hasBand
          ? newTaskWillDoToday ||
            (isPinnedFilterActive
              ? 'pinned'
              : isTodayFilterActive
                ? 'today'
                : false)
          : false;

        // Use createdAt timestamp as default manualOrder
        const newManualOrder = now;

        const urgencyNumber = hasBand ? getBandMidpoint(newTaskBand!) : 5;

        const docRef = await addDoc(collection(db, `users/${user.uid}/tasks`), {
          title: newTaskTitle,
          completed: false,
          completedAt: null,
          categoryId: newTaskCategoryId || '',
          urgency: newTaskBand,
          urgencyNumber,
          urgencyBand: newTaskBand,
          urgencyRank: null, // Will be set after comparison (if banded)
          snoozedUntil: null,
          createdAt: now,
          timezone: timezone,
          willDoToday: willDoToday,
          manualOrder: newManualOrder,
        });

        const newTask: TaskItem = {
          id: docRef.id,
          title: newTaskTitle.trim(),
          completed: false,
          completedAt: undefined,
          categoryId: newTaskCategoryId || '',
          urgency: newTaskBand,
          urgencyNumber,
          urgencyBand: newTaskBand,
          urgencyRank: null,
          snoozedUntil: undefined,
          createdAt: now,
          timezone: userTimezone,
          willDoToday: willDoToday,
          manualOrder: newManualOrder,
        };

        // Add the new task to the existing tasks array, and sort the tasks
        const updatedTasks = sortTasks([...tasks, newTask], categories);
        setTasks(updatedTasks);
        setAllTasksForHeatmap([...allTasksForHeatmap, newTask]);
        setNewTaskTitle('');
        // Keep the chosen band selected so it stays correctly highlighted and
        // subsequent tasks inherit it. Previously this was reset to null, which
        // greyed out the control while still appearing selected and silently
        // dropped new tasks back to the medium default (urgencyNumber 5).

        // Task creation is always friction-free: the new task is captured as
        // "Unranked" (urgencyRank stays null) and never opens the ranking
        // modal — even when an optional Low/Medium/High band is chosen.
        // Ranking happens later via "Rank" on the task card or "Re-rank all"
        // in Settings.
      } catch (error) {
        console.error('Error adding task:', error);
      } finally {
        setIsAddingTask(false);
      }
    }
  };

  const handleEditCategory = async (oldCategory: Category, newName: string) => {
    if (!user) return;

    try {
      await updateDoc(doc(db, `users/${user.uid}/categories`, oldCategory.id), {
        name: newName,
      });

      // Update local state
      setCategories(
        categories.map(cat =>
          cat.id === oldCategory.id ? { ...cat, name: newName } : cat
        )
      );
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteCategory = async (category: Category) => {
    if (!user) return;

    try {
      // Update all tasks with this category to "uncategorized"
      const tasksToUpdate = tasks.filter(
        task => task.categoryId === category.id
      );
      for (const task of tasksToUpdate) {
        await updateDoc(doc(db, `users/${user.uid}/tasks`, task.id), {
          categoryId: 'uncategorized',
        });
      }

      // Delete the category
      await deleteDoc(doc(db, `users/${user.uid}/categories`, category.id));

      // Update local state
      setTasks(
        tasks.map(task =>
          task.categoryId === category.id
            ? { ...task, categoryId: 'uncategorized' }
            : task
        )
      );
      setCategories(categories.filter(cat => cat.id !== category.id));

      // Show red alert
      const alert = document.createElement('div');
      alert.className = 'category-delete-alert';
      alert.textContent = `Category "${category.name}" has been deleted`;
      document.body.appendChild(alert);

      // Remove alert after 2 seconds
      setTimeout(() => {
        alert.remove();
      }, 2000);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleAddCategory = async (name: string): Promise<Category | null> => {
    if (!user) return null;

    try {
      const newCategory = {
        name,
        order: categories.length, // Add new categories at the end
      };
      const docRef = await addDoc(
        collection(db, `users/${user.uid}/categories`),
        newCategory
      );
      const category = { id: docRef.id, name, order: categories.length };
      setCategories([...categories, category]);
      return category;
    } catch (error) {
      console.error('Error adding category:', error);
      return null;
    }
  };

  const handleArchiveCategory = async (category: Category) => {
    if (!user) return;

    try {
      await updateDoc(doc(db, `users/${user.uid}/categories`, category.id), {
        archived: true,
      });

      // Move category from active to archived
      setCategories(categories.filter(cat => cat.id !== category.id));
      setArchivedCategories([
        ...archivedCategories,
        { ...category, archived: true },
      ]);

      // Show temporary alert
      const alert = document.createElement('div');
      alert.className = 'archive-alert';
      alert.textContent = `Category "${category.name}" archived`;
      document.body.appendChild(alert);

      // Remove alert after 2 seconds
      setTimeout(() => {
        alert.remove();
      }, 2000);
    } catch (error) {
      console.error('Error archiving category:', error);
    }
  };

  const handleUnarchiveCategory = async (category: Category) => {
    if (!user) return;

    try {
      await updateDoc(doc(db, `users/${user.uid}/categories`, category.id), {
        archived: false,
      });

      // Move category from archived to active
      setArchivedCategories(
        archivedCategories.filter(cat => cat.id !== category.id)
      );
      setCategories([...categories, { ...category, archived: false }]);

      // Show temporary alert
      const alert = document.createElement('div');
      alert.className = 'archive-alert';
      alert.textContent = `Category "${category.name}" unarchived`;
      document.body.appendChild(alert);

      // Remove alert after 2 seconds
      setTimeout(() => {
        alert.remove();
      }, 2000);
    } catch (error) {
      console.error('Error unarchiving category:', error);
    }
  };

  const handleCategoryChange = async (taskId: string, categoryId: string) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      await updateDoc(doc(db, `users/${user.uid}/tasks`, taskId), {
        categoryId: categoryId,
      });

      setTasks(
        tasks.map(task => (task.id === taskId ? { ...task, categoryId } : task))
      );
    } catch (error) {
      console.error('Error updating task category:', error);
    }
  };

  const handleUrgencyChange = async (taskId: string, urgencyNumber: number) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      // Thresholds: High >= 8, Medium 4-7.9, Low < 4
      const urgency: 'high' | 'medium' | 'low' =
        urgencyNumber >= 8 ? 'high' : urgencyNumber >= 4 ? 'medium' : 'low';
      const updates = {
        urgency: urgency,
        urgencyNumber: urgencyNumber,
        // Clear the band so the exact custom value is preserved. Otherwise the
        // derived-urgency logic clamps unranked banded tasks to their band max
        // (e.g. a medium task can't go above 7.9).
        urgencyBand: null,
        urgencyRank: null,
        hasCustomPriority: true,
      };

      await updateDoc(doc(db, `users/${user.uid}/tasks`, taskId), updates);

      // Note: Urgency is NOT synced - each user maintains their own urgency value

      setTasks(
        tasks.map(task =>
          task.id === taskId
            ? { ...task, urgency, urgencyNumber, urgencyBand: null, urgencyRank: null, hasCustomPriority: true }
            : task
        )
      );

      // Update the modal task if it's open
      if (descriptionModalTask && descriptionModalTask.id === taskId) {
        setDescriptionModalTask({
          ...descriptionModalTask,
          urgency,
          urgencyNumber,
          urgencyBand: null,
          urgencyRank: null,
          hasCustomPriority: true,
        });
      }
    } catch (error) {
      console.error('Error updating task urgency:', error);
    }
  };

  const handleManualOrderChange = async (
    taskId: string,
    direction: 'up' | 'down'
  ) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      const currentTask = tasks.find(task => task.id === taskId);
      if (!currentTask) return;

      // Find all tasks in the same section (completed status, category, urgency, will do today)
      const allTasksInCategory = filterAllTasksInCategory(currentTask);

      if (allTasksInCategory.length === 0) return;

      // Sort tasks in category by current manualOrder (ascending)
      const allTasksInCategorySorted = [...allTasksInCategory].sort((a, b) => {
        const manualOrderA = a.manualOrder || 0;
        const manualOrderB = b.manualOrder || 0;
        return manualOrderA - manualOrderB;
      });

      // Find current task index within category
      const currentTaskIndexWithinCategory = allTasksInCategorySorted.findIndex(
        task => {
          return task.id === taskId;
        }
      );

      // Initialize new manual order
      let newManualOrder: number;

      switch (direction) {
        case 'up':
          // If not first or second element, insert between the two preceding this task
          if (currentTaskIndexWithinCategory > 1) {
            const prevTask =
              allTasksInCategorySorted[currentTaskIndexWithinCategory - 1];
            const prevPrevTask =
              allTasksInCategorySorted[currentTaskIndexWithinCategory - 2];
            newManualOrder =
              ((prevTask.manualOrder || 0) + (prevPrevTask.manualOrder || 0)) /
              2;
          }
          // If the second element, insert right before the first element
          else if (currentTaskIndexWithinCategory === 1) {
            const prevTask = allTasksInCategorySorted[0];
            newManualOrder = (prevTask.manualOrder || 0) - 1000; // Subtract a reasonable amount
          }
          // Otherwise already at the start of the category, so do nothing
          else {
            return;
          }
          break;

        case 'down':
          // If at least two elements exist after this one, insert between them
          if (
            currentTaskIndexWithinCategory >= 0 &&
            currentTaskIndexWithinCategory < allTasksInCategorySorted.length - 2
          ) {
            const nextTask =
              allTasksInCategorySorted[currentTaskIndexWithinCategory + 1];
            const nextNextTask =
              allTasksInCategorySorted[currentTaskIndexWithinCategory + 2];
            newManualOrder =
              ((nextTask.manualOrder || 0) + (nextNextTask.manualOrder || 0)) /
              2;
          }
          // If second last element, insert after the last element
          else if (
            currentTaskIndexWithinCategory ===
            allTasksInCategorySorted.length - 2
          ) {
            const lastTask =
              allTasksInCategorySorted[allTasksInCategorySorted.length - 1];
            const lastManualOrder = lastTask.manualOrder || 0;
            newManualOrder = lastManualOrder + 1000; // Add a reasonable amount
          }
          // Otherwise already at the end of the category, so do nothing
          else {
            return;
          }
          break;
      }

      // Update the task in Firebase
      await updateDoc(doc(db, `users/${user.uid}/tasks`, taskId), {
        manualOrder: newManualOrder,
      });

      // Update local state
      setTasks(
        tasks.map(task => {
          if (task.id === taskId) {
            return { ...task, manualOrder: newManualOrder };
          }
          return task;
        })
      );
    } catch (error) {
      console.error('Error updating task manualOrder:', error);
    }
  };

  const handleSnooze = async (id: string, snoozedUntil: number) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      await updateDoc(doc(db, `users/${user.uid}/tasks`, id), {
        snoozedUntil: snoozedUntil,
      });

      setTasks(
        tasks.map(task =>
          task.id === id ? { ...task, snoozedUntil: snoozedUntil } : task
        )
      );

      // Format the alert date
      const snoozeMoment = moment(snoozedUntil);
      let displayDate: string;

      // Use calendar time for up to a week out
      displayDate = snoozeMoment.calendar(undefined, {
        sameDay: '[today]',
        nextDay: '[tomorrow]',
        nextWeek: 'dddd',
        lastDay: '[yesterday]',
        lastWeek: '[last] dddd',
        sameElse: 'MMM D, YYYY',
      });

      // Show temporary alert
      const alert = document.createElement('div');
      alert.className = 'snooze-alert';
      alert.textContent = `Task snoozed until ${displayDate}`;
      document.body.appendChild(alert);

      // Remove alert after 2 seconds
      setTimeout(() => {
        alert.remove();
      }, 2000);
    } catch (error) {
      console.error('Error snoozing task:', error);
    }
  };

  const handleUnsnooze = async (taskId: string) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      await updateDoc(doc(db, `users/${user.uid}/tasks`, taskId), {
        snoozedUntil: null,
      });

      setTasks(
        tasks.map(task =>
          task.id === taskId ? { ...task, snoozedUntil: undefined } : task
        )
      );

      // Show temporary alert
      const alert = document.createElement('div');
      alert.className = 'snooze-alert';
      alert.textContent = 'Task unsnoozed';
      document.body.appendChild(alert);

      // Remove alert after 2 seconds
      setTimeout(() => {
        alert.remove();
      }, 2000);
    } catch (error) {
      console.error('Error unsnoozing task:', error);
    }
  };

  const handleRecurrenceChange = async (id: string, recurrenceDays: number | null) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      await updateDoc(doc(db, `users/${user.uid}/tasks`, id), {
        recurrenceDays: recurrenceDays,
      });

      setTasks(
        tasks.map(task =>
          task.id === id
            ? { ...task, recurrenceDays: recurrenceDays ?? undefined }
            : task
        )
      );

      if (descriptionModalTask && descriptionModalTask.id === id) {
        setDescriptionModalTask({
          ...descriptionModalTask,
          recurrenceDays: recurrenceDays ?? undefined,
        });
      }

      // Show alert
      const alert = document.createElement('div');
      alert.className = 'snooze-alert';
      alert.textContent = recurrenceDays
        ? `Task will repeat every ${recurrenceDays} day${recurrenceDays > 1 ? 's' : ''}`
        : 'Recurrence removed';
      document.body.appendChild(alert);
      setTimeout(() => alert.remove(), 2000);
    } catch (error) {
      console.error('Error updating recurrence:', error);
    }
  };

  const handleCompletionLogChange = async (id: string, newCompletionLog: number[]) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      const task = tasks.find(t => t.id === id);
      if (!task) return;

      const oldLog = task.completionLog || [];
      const deletedMostRecent = oldLog.length > 0 &&
        newCompletionLog.length < oldLog.length &&
        !newCompletionLog.includes(oldLog[oldLog.length - 1]);

      // If deleted the most recent entry and task is snoozed, un-snooze it
      const shouldUnsnooze = deletedMostRecent && task.snoozedUntil;

      const updates: { completionLog: number[]; snoozedUntil?: null } = {
        completionLog: newCompletionLog,
      };
      if (shouldUnsnooze) {
        updates.snoozedUntil = null;
      }

      await updateDoc(doc(db, `users/${user.uid}/tasks`, id), updates);

      const updatedTask = {
        ...task,
        completionLog: newCompletionLog,
        ...(shouldUnsnooze ? { snoozedUntil: undefined } : {}),
      };

      setTasks(
        tasks.map(t => t.id === id ? updatedTask : t)
      );

      if (descriptionModalTask && descriptionModalTask.id === id) {
        setDescriptionModalTask(updatedTask);
      }

      if (shouldUnsnooze) {
        const alert = document.createElement('div');
        alert.className = 'snooze-alert';
        alert.textContent = 'Task un-snoozed';
        document.body.appendChild(alert);
        setTimeout(() => alert.remove(), 2000);
      }
    } catch (error) {
      console.error('Error updating completion log:', error);
    }
  };

  const handleToggleDontExpireTasks = async () => {
    if (!isFirebaseInitialized || !user) return;

    // Toggle the "don't expire" setting
    const newValue = todayExtendedUntil ? null : 'permanent';
    setTodayExtendedUntil(newValue);

    try {
      const userPrefsRef = doc(db, `users/${user.uid}/preferences`, 'general');
      await setDoc(
        userPrefsRef,
        { todayExtendedUntil: newValue },
        { merge: true }
      );
    } catch (error) {
      console.error('Error saving todayExtendedUntil:', error);
    }
  };

  // Stack ranking comparison handlers
  const getComparisonCandidates = useCallback(
    (band: UrgencyBand, excludeTaskId?: string, filterCategoryId?: string): TaskItem[] => {
      return tasks
        .filter(
          t =>
            !t.completed &&
            (t.urgencyBand === band ||
              (t.urgencyBand === undefined && getBandFromNumber(t.urgencyNumber ?? 5) === band)) &&
            t.id !== excludeTaskId &&
            (filterCategoryId === undefined || t.categoryId === filterCategoryId)
        )
        .sort((a, b) => (a.urgencyRank ?? 999) - (b.urgencyRank ?? 999));
    },
    [tasks]
  );

  // Record a comparison relationship: moreUrgentTaskId is more urgent than lessUrgentTaskId
  // Also removes any contradicting relationship (overwrite behavior)
  const recordComparison = useCallback(
    async (moreUrgentTaskId: string, lessUrgentTaskId: string) => {
      if (!user) return;

      try {
        const batch = writeBatch(db);
        const tasksRef = collection(db, 'users', user.uid, 'tasks');

        // 1. Add relationship to moreUrgentTask
        const moreUrgentTask = tasks.find(t => t.id === moreUrgentTaskId);
        const currentRelations = moreUrgentTask?.moreUrgentThan || [];
        let updatedMoreUrgentRelations = currentRelations;

        if (!currentRelations.includes(lessUrgentTaskId)) {
          updatedMoreUrgentRelations = [...currentRelations, lessUrgentTaskId];
          batch.update(doc(tasksRef, moreUrgentTaskId), {
            moreUrgentThan: updatedMoreUrgentRelations,
          });
        }

        // 2. Remove contradicting relationship from lessUrgentTask (overwrite)
        const lessUrgentTask = tasks.find(t => t.id === lessUrgentTaskId);
        const lessUrgentRelations = lessUrgentTask?.moreUrgentThan || [];
        let updatedLessUrgentRelations = lessUrgentRelations;

        if (lessUrgentRelations.includes(moreUrgentTaskId)) {
          updatedLessUrgentRelations = lessUrgentRelations.filter(
            id => id !== moreUrgentTaskId
          );
          batch.update(doc(tasksRef, lessUrgentTaskId), {
            moreUrgentThan: updatedLessUrgentRelations,
          });
        }

        await batch.commit();

        // Update local state
        setTasks(prev =>
          prev.map(t => {
            if (t.id === moreUrgentTaskId) {
              return { ...t, moreUrgentThan: updatedMoreUrgentRelations };
            }
            if (t.id === lessUrgentTaskId) {
              return { ...t, moreUrgentThan: updatedLessUrgentRelations };
            }
            return t;
          })
        );
      } catch (error) {
        console.error('Error recording comparison:', error);
      }
    },
    [user, tasks]
  );

  const handleFinalizeRank = async (
    taskId: string,
    rank: number,
    band: UrgencyBand
  ) => {
    if (!user) return;

    try {
      const tasksRef = collection(db, 'users', user.uid, 'tasks');

      // Get all tasks in this band that need rank adjustment
      const candidatesInBand = getComparisonCandidates(band, taskId);

      // Batch update: increment rank for tasks at or below insertion point
      const batch = writeBatch(db);

      // Update the new task with its rank and band
      const newTaskRef = doc(tasksRef, taskId);
      batch.update(newTaskRef, {
        urgencyBand: band,
        urgencyRank: rank,
        hasCustomPriority: false,
      });

      // Shift ranks for tasks at or below the insertion point (only ranked tasks)
      for (const task of candidatesInBand) {
        const currentRank = task.urgencyRank;
        if (currentRank == null) continue; // Skip unranked tasks
        if (currentRank >= rank) {
          const taskRef = doc(tasksRef, task.id);
          batch.update(taskRef, {
            urgencyRank: currentRank + 1,
            urgencyBand: band,
          });
        }
      }

      await batch.commit();
    } catch (error) {
      console.error('Failed to update rank in Firestore:', error);
      return; // Don't update local state if Firestore failed
    }

    // Update local state
    setTasks(prev =>
      prev.map(t => {
        if (t.id === taskId) {
          return { ...t, urgencyBand: band, urgencyRank: rank, hasCustomPriority: false };
        }
        // Only bump ranks for tasks that have already been ranked
        const currentRank = t.urgencyRank;
        if (currentRank == null) return t; // Skip unranked tasks
        if (
          (t.urgencyBand === band ||
            getBandFromNumber(t.urgencyNumber ?? 5) === band) &&
          currentRank >= rank &&
          t.id !== taskId
        ) {
          return { ...t, urgencyRank: currentRank + 1, urgencyBand: band };
        }
        return t;
      })
    );

    // Clean up comparison state (but keep modal open if showing celebration)
    setComparisonSession(null);
    setPendingNewTask(null);
  };

  // Keep ref updated with latest function
  handleFinalizeRankRef.current = handleFinalizeRank;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const startComparisonSession = useCallback(
    (task: TaskItem, band: UrgencyBand, filterCategoryId?: string) => {
      const candidates = getComparisonCandidates(band, task.id, filterCategoryId);

      if (candidates.length === 0) {
        // No other tasks in band - assign rank 1 directly
        handleFinalizeRankRef.current?.(task.id, 1, band);
        return;
      }

      const candidateIds = candidates.map(c => c.id);

      // If we have a preference graph, check if position is already known
      if (preferenceGraph) {
        const knownPos = findKnownPosition(preferenceGraph, task.id, candidateIds);

        if (knownPos?.isComplete) {
          // Position is fully determined from graph - no comparisons needed!
          console.log(`Skipping comparisons for task ${task.id} - position known from graph`);
          setComparisonsSkipped(prev => prev + Math.ceil(Math.log2(candidates.length + 1)));
          handleFinalizeRankRef.current?.(task.id, knownPos.rank, band);
          return;
        }

        // Filter to only candidates with unknown relationships
        const { unknowns, knownLessThan } = filterToUnknownCandidates(
          preferenceGraph,
          task.id,
          candidateIds
        );

        if (unknowns.length === 0) {
          // All relationships known - calculate position
          const rank = knownLessThan.length + 1;
          console.log(`All relationships known for task ${task.id} - rank ${rank}`);
          setComparisonsSkipped(prev => prev + Math.ceil(Math.log2(candidates.length + 1)));
          handleFinalizeRankRef.current?.(task.id, rank, band);
          return;
        }

        // Some relationships known - use filtered candidates
        // But we need to track offset for final rank calculation
        const session = createComparisonSession(task.id, band, unknowns);
        // Store the offset (how many tasks we're already known to be less urgent than)
        (session as ComparisonSession & { rankOffset?: number }).rankOffset = knownLessThan.length;
        // Store the full candidates list for rank calculation
        (session as ComparisonSession & { fullCandidates?: string[] }).fullCandidates = candidateIds;
        // Store filterCategoryId for category-scoped priority calculation
        if (filterCategoryId) {
          (session as ComparisonSession & { filterCategoryId?: string }).filterCategoryId = filterCategoryId;
        }

        setPendingNewTask(task);
        setComparisonSession(session);
        setComparisonNumber(1);
        setIsComparisonModalOpen(true);
        return;
      }

      // No preference graph - use standard binary search
      const session = createComparisonSession(task.id, band, candidateIds);
      // Store filterCategoryId for category-scoped priority calculation
      if (filterCategoryId) {
        (session as ComparisonSession & { filterCategoryId?: string }).filterCategoryId = filterCategoryId;
      }

      setPendingNewTask(task);
      setComparisonSession(session);
      setComparisonNumber(1);
      setIsComparisonModalOpen(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getComparisonCandidates, preferenceGraph]
  );

  // Handlers for comparison scope modal (category vs all tasks)
  const handleComparisonScopeCategory = useCallback(() => {
    if (pendingTaskForScopeChoice && pendingBandForScopeChoice) {
      setIsComparisonScopeModalOpen(false);
      startComparisonSession(
        pendingTaskForScopeChoice,
        pendingBandForScopeChoice,
        selectedCategoryId // Filter to current category
      );
      setPendingTaskForScopeChoice(null);
      setPendingBandForScopeChoice(null);
    }
  }, [pendingTaskForScopeChoice, pendingBandForScopeChoice, selectedCategoryId, startComparisonSession]);

  const handleComparisonScopeAll = useCallback(() => {
    if (pendingTaskForScopeChoice && pendingBandForScopeChoice) {
      setIsComparisonScopeModalOpen(false);
      startComparisonSession(
        pendingTaskForScopeChoice,
        pendingBandForScopeChoice
        // No category filter - compare against all
      );
      setPendingTaskForScopeChoice(null);
      setPendingBandForScopeChoice(null);
    }
  }, [pendingTaskForScopeChoice, pendingBandForScopeChoice, startComparisonSession]);

  const handleComparisonScopeCancel = useCallback(() => {
    setIsComparisonScopeModalOpen(false);
    setPendingTaskForScopeChoice(null);
    setPendingBandForScopeChoice(null);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const handleComparisonChoice = useCallback(
    (choice: 'left' | 'right' | 'skip' | 'too-tough') => {
      if (!comparisonSession || !pendingNewTask) return;

      // Record the comparison relationship before processing
      if (choice === 'left' || choice === 'right') {
        const pivotIndex = getPivotIndex(comparisonSession);
        const pivotTaskId = comparisonSession.candidates[pivotIndex];

        if (choice === 'left') {
          // New task is more urgent than pivot
          recordComparison(pendingNewTask.id, pivotTaskId);
          // Update preference graph with transitivity
          if (preferenceGraph) {
            addComparisonToGraph(preferenceGraph, pendingNewTask.id, pivotTaskId);
          }
        } else {
          // Pivot is more urgent than new task
          recordComparison(pivotTaskId, pendingNewTask.id);
          // Update preference graph with transitivity
          if (preferenceGraph) {
            addComparisonToGraph(preferenceGraph, pivotTaskId, pendingNewTask.id);
          }
        }
      }

      const updatedSession = processChoice(comparisonSession, choice);

      if (isSessionConverged(updatedSession)) {
        const band = updatedSession.band;
        const taskId = pendingNewTask.id;
        const taskTitle = pendingNewTask.title;

        // Get session metadata
        const sessionWithMeta = comparisonSession as ComparisonSession & {
          rankOffset?: number;
          fullCandidates?: string[];
        };
        const rankOffset = sessionWithMeta.rankOffset || 0;
        // Use fullCandidates if available (preference graph case), otherwise use session candidates
        const allCandidateIds = sessionWithMeta.fullCandidates || comparisonSession.candidates;

        // Get the actual candidate tasks to look up their urgencyRanks
        const candidateTasks = allCandidateIds
          .map(id => tasks.find(t => t.id === id))
          .filter((t): t is TaskItem => t != null)
          .sort((a, b) => (a.urgencyRank ?? 999) - (b.urgencyRank ?? 999));

        // Calculate insertion position
        // lowIndex is the position in the compared candidates (may be filtered by preference graph)
        // We need to translate this to a position in the full candidate list
        const comparedInsertionIndex = updatedSession.lowIndex;

        // If preference graph was used, we need to account for known relationships
        // The compared candidates are a subset of allCandidateIds
        // insertionIndex in the full list = comparedInsertionIndex + rankOffset
        const fullInsertionIndex = comparedInsertionIndex + rankOffset;

        // Calculate final rank based on actual candidate urgencyRanks
        let finalRank: number;
        if (candidateTasks.length === 0) {
          // No candidates - this is the first task in the band
          finalRank = 1;
        } else if (fullInsertionIndex === 0) {
          // Most urgent - insert before the first candidate
          finalRank = candidateTasks[0].urgencyRank ?? 1;
        } else if (fullInsertionIndex >= candidateTasks.length) {
          // Least urgent - insert after the last candidate
          const lastRank = candidateTasks[candidateTasks.length - 1].urgencyRank ?? candidateTasks.length;
          finalRank = lastRank + 1;
        } else {
          // In the middle - insert before the candidate at fullInsertionIndex
          finalRank = candidateTasks[fullInsertionIndex].urgencyRank ?? (fullInsertionIndex + 1);
        }

        // Check if this was a category-scoped comparison
        const filterCategoryId = (sessionWithMeta as ComparisonSession & { filterCategoryId?: string }).filterCategoryId;

        // Calculate totalInBand and position based on scope
        // If category-scoped, use category totals for score calculation
        const allRankedInBand = tasks.filter(
          t => !t.completed &&
               t.id !== taskId &&
               t.urgencyRank != null &&
               (t.urgencyBand === band || (!t.urgencyBand && getBandFromNumber(t.urgencyNumber ?? 5) === band)) &&
               (filterCategoryId === undefined || t.categoryId === filterCategoryId)
        );
        const totalInBand = allRankedInBand.length + 1; // +1 for the new task

        // Calculate position within scope
        // For category-scoped: position among category tasks only
        // For global: position among all tasks in band
        const tasksBeforeNewTask = allRankedInBand.filter(t => (t.urgencyRank ?? 999) < finalRank).length;
        const positionInScope = tasksBeforeNewTask + 1; // 1-indexed position

        // Calculate the urgency score based on position within scope
        const score = deriveUrgencyNumber(positionInScope, band, totalInBand);

        // Finalize the rank immediately (uses global rank for proper shifting)
        handleFinalizeRankRef.current?.(taskId, finalRank, band);

        // Show celebration (user dismisses by clicking outside)
        setCelebrationData({
          score,
          band,
          rank: positionInScope, // Show position in scope, not global rank
          totalInBand,
          taskTitle,
          taskId,
        });
      } else {
        setComparisonSession(updatedSession);
        setComparisonNumber(prev => prev + 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comparisonSession, pendingNewTask, recordComparison, preferenceGraph, tasks]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const handleComparisonUndo = useCallback(() => {
    if (!comparisonSession) return;

    const previousSession = undoLastChoice(comparisonSession);
    if (previousSession) {
      setComparisonSession(previousSession);
      setComparisonNumber(prev => Math.max(1, prev - 1));
    }
  }, [comparisonSession]);

  // Auto-progression for re-rank-all mode
  useEffect(() => {
    if (!celebrationData || rerankAllTotal === 0) return;

    const timer = setTimeout(() => {
      setCelebrationData(null);
      setRerankAllCompleted(prev => prev + 1);

      if (rerankQueue.length > 0) {
        // Start next task
        const nextTaskId = rerankQueue[0];
        const nextTask = tasks.find(t => t.id === nextTaskId);
        if (nextTask) {
          setRerankQueue(prev => prev.slice(1));
          const nextBand = nextTask.urgencyBand || getBandFromNumber(nextTask.urgencyNumber ?? 5);
          startComparisonSession(nextTask, nextBand);
        }
      } else {
        // All done
        setRerankAllComplete(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [celebrationData, rerankAllTotal, rerankQueue, tasks, startComparisonSession]);

  const handleMarkDoneFromComparison = useCallback(
    async (taskId: string) => {
      // Mark the task as done
      await handleToggle(taskId);

      // If the task being ranked (left/newTask) is marked done, close modal
      if (pendingNewTask && taskId === pendingNewTask.id) {
        setIsComparisonModalOpen(false);
        setPendingNewTask(null);
        setComparisonSession(null);

        // Check for next task in rerank queue
        if (rerankQueue.length > 0) {
          const nextTaskId = rerankQueue[0];
          const nextTask = tasks.find(t => t.id === nextTaskId);
          if (nextTask) {
            setRerankQueue(prev => prev.slice(1));
            const nextBand = nextTask.urgencyBand || getBandFromNumber(nextTask.urgencyNumber ?? 5);
            setTimeout(() => {
              startComparisonSession(nextTask, nextBand);
            }, 100);
          }
        }
        return;
      }

      // If the pivot task (right) is marked done, skip to next comparison
      if (comparisonSession) {
        const pivotIndex = getPivotIndex(comparisonSession);
        const pivotTaskId = comparisonSession.candidates[pivotIndex];

        if (taskId === pivotTaskId) {
          // Remove this candidate from the session
          const newCandidates = comparisonSession.candidates.filter(id => id !== taskId);

          if (newCandidates.length === 0 && pendingNewTask) {
            // No candidates left - finalize at rank 1
            const band = pendingNewTask.urgencyBand || getBandFromNumber(pendingNewTask.urgencyNumber ?? 5);
            handleFinalizeRankRef.current?.(pendingNewTask.id, 1, band);
            setIsComparisonModalOpen(false);
            return;
          }

          // Create updated session with the candidate removed
          // After removing pivotIndex, indices after it shift down by 1
          // lowIndex stays same (it's <= pivotIndex), highIndex decreases by 1
          const updatedSession: ComparisonSession = {
            ...comparisonSession,
            candidates: newCandidates,
            highIndex: comparisonSession.highIndex - 1,
          };

          if (isSessionConverged(updatedSession) && pendingNewTask) {
            const finalRank = getFinalRank(updatedSession);
            const band = pendingNewTask.urgencyBand || getBandFromNumber(pendingNewTask.urgencyNumber ?? 5);
            handleFinalizeRankRef.current?.(pendingNewTask.id, finalRank, band);
            setIsComparisonModalOpen(false);
          } else {
            setComparisonSession(updatedSession);
          }
        }
      }
    },
    [handleToggle, pendingNewTask, comparisonSession, rerankQueue, tasks, startComparisonSession]
  );

  const handleRerank = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const band = task.urgencyBand || getBandFromNumber(task.urgencyNumber ?? 5);

      // Remove this task's rank temporarily
      setTasks(prev =>
        prev.map(t =>
          t.id === taskId ? { ...t, urgencyRank: null } : t
        )
      );

      // Start comparison session
      startComparisonSession(task, band);
    },
    [tasks, startComparisonSession]
  );

  // Apply final merge-sort order to tasks in Firestore
  const applySmartRankingFinalOrder = useCallback(
    async (sortedTaskIds: string[]) => {
      if (!user || sortedTaskIds.length === 0) return;

      const totalTasks = sortedTaskIds.length;

      try {
        const batch = writeBatch(db);
        const tasksRef = collection(db, 'users', user.uid, 'tasks');

        // Assign ranks and urgencyNumber based on sorted order
        // Rank 1 = highest priority = urgencyNumber 10
        // Rank n = lowest priority = urgencyNumber 0
        sortedTaskIds.forEach((taskId, index) => {
          const rank = index + 1;
          // Map rank to urgencyNumber: rank 1 -> 10, rank n -> 0
          const urgencyNumber = totalTasks === 1 ? 10 : 10 - (index / (totalTasks - 1)) * 10;
          const taskRef = doc(tasksRef, taskId);
          batch.update(taskRef, {
            urgencyRank: rank,
            urgencyNumber: Math.round(urgencyNumber * 10) / 10, // Round to 1 decimal
            urgencyBand: urgencyNumber >= 8 ? 'high' : urgencyNumber >= 4 ? 'medium' : 'low',
            hasCustomPriority: false, // Priority is now set by ranking, not manual
          });
        });

        await batch.commit();

        // Update local state and re-sort
        setTasks(prev => {
          const updatedTasks = prev.map(t => {
            const sortIndex = sortedTaskIds.indexOf(t.id);
            if (sortIndex >= 0) {
              const urgencyNumber = totalTasks === 1 ? 10 : 10 - (sortIndex / (totalTasks - 1)) * 10;
              const roundedUrgency = Math.round(urgencyNumber * 10) / 10;
              return {
                ...t,
                urgencyRank: sortIndex + 1,
                urgencyNumber: roundedUrgency,
                urgencyBand: (roundedUrgency >= 8 ? 'high' : roundedUrgency >= 4 ? 'medium' : 'low') as 'high' | 'medium' | 'low',
                hasCustomPriority: false,
              };
            }
            return t;
          });
          // Re-sort tasks to reflect new priorities
          return sortTasks(updatedTasks, categories);
        });

        console.log('applySmartRankingFinalOrder: applied ranks to', sortedTaskIds.length, 'tasks');
      } catch (error) {
        console.error('Error applying merge sort order:', error);
      }
    },
    [user, categories]
  );

  // === RERANK FLOW HANDLERS ===

  const startRerankFlow = useCallback(async () => {
    if (!user) return;

    // Get all non-completed task IDs
    const taskIds = tasks
      .filter(t => !t.completed)
      .map(t => t.id);

    if (taskIds.length === 0) {
      const alert = document.createElement('div');
      alert.className = 'delete-alert';
      alert.textContent = 'No tasks to re-rank';
      document.body.appendChild(alert);
      setTimeout(() => alert.remove(), 2000);
      return;
    }

    // Check for existing saved state in Firestore
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const savedStateJson = userDoc.data()?.rerankStateJson;

      if (savedStateJson) {
        const savedState = deserializeRerankState(savedStateJson);
        if (savedState) {
          // Resume from saved state
          setRerankState(savedState);
          if (savedState.tournamentState && !isTournamentComplete(savedState.tournamentState)) {
            setIsTournamentModalOpen(true);
          } else if (savedState.bucketingState && !isBucketingComplete(savedState.bucketingState)) {
            setIsBucketingModalOpen(true);
          } else if (savedState.bucketingState && isBucketingComplete(savedState.bucketingState)) {
            // Bucketing done, tournament not started - show transition
            setIsBucketingModalOpen(true);
          }
          return;
        }
      }
    } catch (error) {
      console.error('Error loading saved rerank state:', error);
    }

    // Initialize fresh bucketing state
    const bucketingState = initializeBucketing(taskIds);
    setRerankState({
      bucketingState,
      tournamentState: null,
      bucketSummary: null,
    });
    setIsBucketingModalOpen(true);
  }, [tasks, user]);

  const handleBucketingStateChange = useCallback((newBucketingState: BucketingState) => {
    setRerankState(prev => ({
      ...prev!,
      bucketingState: newBucketingState,
      tournamentState: null,
      bucketSummary: null,
    }));
  }, []);

  const handleBucketingComplete = useCallback(async (finalState: BucketingState) => {
    if (!user) return;

    // Default urgencyNumber for each bucket (top of range)
    const bucketDefaults = {
      high: 10,
      medium: 7,
      low: 3,
    };

    // Apply bucket decisions to tasks
    const batch = writeBatch(db);
    const tasksToDelete: string[] = [];
    const tasksToComplete: string[] = [];

    for (const [taskId, decision] of Object.entries(finalState.decisions)) {
      const taskRef = doc(db, 'users', user.uid, 'tasks', taskId);

      if (decision.bucket === 'delete') {
        tasksToDelete.push(taskId);
        batch.delete(taskRef);
      } else if (decision.bucket === 'done') {
        tasksToComplete.push(taskId);
        batch.update(taskRef, {
          completed: true,
          completedAt: Date.now(),
          hasCustomPriority: false,
        });
      } else {
        // Normal bucket assignment (high, medium, low)
        const urgencyNumber = bucketDefaults[decision.bucket as 'high' | 'medium' | 'low'];
        batch.update(taskRef, {
          urgencyBand: decision.bucket,
          urgencyNumber,
          urgencyRank: null,
          willDoToday: decision.willDoToday ? 'today' : false,
          hasCustomPriority: false,
        });
      }
    }

    try {
      await batch.commit();
    } catch (error) {
      console.error('Error applying bucketing decisions:', error);
      return;
    }

    // Update local state
    setTasks(prev => prev
      .filter(task => !tasksToDelete.includes(task.id))
      .map(task => {
        const decision = finalState.decisions[task.id];
        if (!decision) return task;

        if (decision.bucket === 'done') {
          return {
            ...task,
            completed: true,
            completedAt: Date.now(),
            hasCustomPriority: false,
          };
        }

        // Normal bucket (high, medium, low)
        const urgencyNumber = bucketDefaults[decision.bucket as 'high' | 'medium' | 'low'];
        return {
          ...task,
          urgencyBand: decision.bucket as 'high' | 'medium' | 'low',
          urgencyNumber,
          urgencyRank: null,
          willDoToday: decision.willDoToday ? 'today' : false,
          hasCustomPriority: false,
        };
      }));

    setRerankState(null);
    setIsBucketingModalOpen(false);
  }, [user]);

  const handleTournamentStateChange = useCallback((newTournamentState: TournamentState) => {
    setRerankState(prev => ({
      ...prev!,
      tournamentState: newTournamentState,
    }));
  }, []);

  const handleMarkTaskDoneInTournament = useCallback(async (taskId: string) => {
    if (!user || !rerankState?.tournamentState) return;

    // Mark task as completed in Firestore
    try {
      const taskRef = doc(db, 'users', user.uid, 'tasks', taskId);
      await setDoc(taskRef, {
        completed: true,
        completedAt: Date.now(),
      }, { merge: true });
    } catch (error) {
      console.error('Error marking task as done:', error);
      return;
    }

    // Update local task state
    setTasks(prev => prev.map(task =>
      task.id === taskId
        ? { ...task, completed: true, completedAt: Date.now() }
        : task
    ));

    // Remove task from tournament and regenerate current round matches
    const currentState = rerankState.tournamentState;
    const remainingTasks = currentState.tasks.filter(id => id !== taskId);

    // If only 1 or 0 tasks left, end tournament
    if (remainingTasks.length <= 1) {
      setRerankState(prev => ({
        ...prev!,
        tournamentState: {
          ...currentState,
          tasks: remainingTasks,
          currentRound: currentState.totalRounds + 1, // Mark as complete
          matches: [],
        },
      }));
      return;
    }

    // Remove the task from results and eliminated lists
    const newResults = { ...currentState.results };
    delete newResults[taskId];

    const newEliminated = currentState.eliminatedTasks.filter(id => id !== taskId);

    // Regenerate matches for current round without this task
    // Import the helper to get completed matchups
    const completedMatchups = new Set<string>();
    for (const result of currentState.history) {
      const key = result.match.taskA < result.match.taskB
        ? `${result.match.taskA}|${result.match.taskB}`
        : `${result.match.taskB}|${result.match.taskA}`;
      completedMatchups.add(key);
    }

    // Get active tasks (not eliminated, not the removed one)
    const activeTasks = remainingTasks.filter(id => !newEliminated.includes(id));

    // Generate new matches avoiding completed matchups
    const newMatches: Array<{ taskA: string; taskB: string; winner: string | null }> = [];
    const paired = new Set<string>();

    const sorted = [...activeTasks].sort((a, b) => {
      const aScore = (newResults[a]?.wins || 0) - (newResults[a]?.losses || 0);
      const bScore = (newResults[b]?.wins || 0) - (newResults[b]?.losses || 0);
      return bScore - aScore;
    });

    for (let i = 0; i < sorted.length; i++) {
      const taskA = sorted[i];
      if (paired.has(taskA)) continue;

      for (let j = i + 1; j < sorted.length; j++) {
        const taskB = sorted[j];
        if (paired.has(taskB)) continue;

        const key = taskA < taskB ? `${taskA}|${taskB}` : `${taskB}|${taskA}`;
        if (!completedMatchups.has(key)) {
          newMatches.push({ taskA, taskB, winner: null });
          paired.add(taskA);
          paired.add(taskB);
          break;
        }
      }
    }

    setRerankState(prev => ({
      ...prev!,
      tournamentState: {
        ...currentState,
        tasks: remainingTasks,
        results: newResults,
        eliminatedTasks: newEliminated,
        matches: newMatches,
        currentMatchIndex: 0,
      },
    }));
  }, [user, rerankState]);

  const handleTournamentComplete = useCallback(async (
    rankings: Array<{ taskId: string; rank: number }>
  ) => {
    if (!user) return;

    const batch = writeBatch(db);
    const tasksToDelete: string[] = [];
    const tasksToComplete: string[] = [];

    // Determine which bucket we're ranking
    // If rerankingBucket is set, we're doing a single-bucket tournament
    // Otherwise, we came from full bucketing flow and are ranking 'high'
    const targetBucket = rerankingBucket || 'high';

    // Calculate urgencyNumber range based on bucket
    // High: 10.0 -> 8.0, Medium: 7.0 -> 4.0, Low: 3.0 -> 0.0
    const bucketRange = {
      high: { max: 10, min: 8 },
      medium: { max: 7, min: 4 },
      low: { max: 3, min: 0 },
    };
    const range = bucketRange[targetBucket];
    const spread = range.max - range.min;

    // Apply rankings to tournament tasks
    const totalRanked = rankings.length;
    for (const { taskId, rank } of rankings) {
      const urgencyNumber = totalRanked === 1
        ? range.max
        : range.max - ((rank - 1) / (totalRanked - 1)) * spread;
      const taskRef = doc(db, 'users', user.uid, 'tasks', taskId);
      batch.update(taskRef, {
        urgencyRank: rank,
        urgencyNumber: Math.round(urgencyNumber * 10) / 10,
        urgencyBand: targetBucket,
        hasCustomPriority: false,
      });
    }

    // Get bucket counts for calculating urgencyNumber spread within each bucket
    const bucketCounts = { high: 0, medium: 0, low: 0 };

    // If we came from full bucketing flow, also apply medium/low bucket decisions
    if (rerankState?.bucketingState) {
      for (const [, decision] of Object.entries(rerankState.bucketingState.decisions)) {
        if (decision.bucket === 'high' || decision.bucket === 'medium' || decision.bucket === 'low') {
          bucketCounts[decision.bucket]++;
        }
      }

      let mediumRank = 1;
      let lowRank = 1;

      for (const [taskId, decision] of Object.entries(rerankState.bucketingState.decisions)) {
        const taskRef = doc(db, 'users', user.uid, 'tasks', taskId);

        if (decision.bucket === 'delete') {
          tasksToDelete.push(taskId);
          batch.delete(taskRef);
        } else if (decision.bucket === 'done') {
          tasksToComplete.push(taskId);
          batch.update(taskRef, {
            completed: true,
            completedAt: Date.now(),
            hasCustomPriority: false,
          });
        } else if (decision.bucket === 'medium') {
          const mediumCount = bucketCounts.medium;
          // Medium: 7.0 -> 4.0 (spread of 3)
          const urgencyNumber = mediumCount === 1 ? 7 : 7 - ((mediumRank - 1) / (mediumCount - 1)) * 3;
          batch.update(taskRef, {
            urgencyBand: 'medium',
            urgencyNumber: Math.round(urgencyNumber * 10) / 10,
            urgencyRank: mediumRank,
            willDoToday: decision.willDoToday ? 'today' : false,
            hasCustomPriority: false,
          });
          mediumRank++;
        } else if (decision.bucket === 'low') {
          const lowCount = bucketCounts.low;
          // Low: 3.0 -> 0.0 (spread of 3)
          const urgencyNumber = lowCount === 1 ? 3 : 3 - ((lowRank - 1) / (lowCount - 1)) * 3;
          batch.update(taskRef, {
            urgencyBand: 'low',
            urgencyNumber: Math.round(urgencyNumber * 10) / 10,
            urgencyRank: lowRank,
            willDoToday: decision.willDoToday ? 'today' : false,
            hasCustomPriority: false,
          });
          lowRank++;
        }
        // Note: 'high' bucket tasks are handled above via rankings
      }
    }

    try {
      await batch.commit();
    } catch (error) {
      console.error('Error applying rankings:', error);
      return;
    }

    // Update local state
    const rankingsMap = new Map(rankings.map(r => [r.taskId, r.rank]));
    let localMediumRank = 1;
    let localLowRank = 1;

    setTasks(prev => prev
      .filter(task => !tasksToDelete.includes(task.id))
      .map(task => {
        const ranking = rankingsMap.get(task.id);
        const decision = rerankState?.bucketingState?.decisions[task.id];

        if (decision?.bucket === 'done') {
          return {
            ...task,
            completed: true,
            completedAt: Date.now(),
            hasCustomPriority: false,
          };
        }

        // Task was in the tournament
        if (ranking !== undefined) {
          const urgencyNumber = totalRanked === 1
            ? range.max
            : range.max - ((ranking - 1) / (totalRanked - 1)) * spread;
          return {
            ...task,
            urgencyRank: ranking,
            urgencyNumber: Math.round(urgencyNumber * 10) / 10,
            urgencyBand: targetBucket,
            hasCustomPriority: false,
          };
        }

        // Medium bucket (from full bucketing flow)
        if (decision?.bucket === 'medium') {
          const mediumCount = bucketCounts.medium;
          const rank = localMediumRank++;
          // Medium: 7.0 -> 4.0 (spread of 3)
          const urgencyNumber = mediumCount === 1 ? 7 : 7 - ((rank - 1) / (mediumCount - 1)) * 3;
          return {
            ...task,
            urgencyBand: 'medium' as const,
            urgencyNumber: Math.round(urgencyNumber * 10) / 10,
            urgencyRank: rank,
            willDoToday: decision.willDoToday ? 'today' : false,
            hasCustomPriority: false,
          };
        }

        // Low bucket (from full bucketing flow)
        if (decision?.bucket === 'low') {
          const lowCount = bucketCounts.low;
          const rank = localLowRank++;
          // Low: 3.0 -> 0.0 (spread of 3)
          const urgencyNumber = lowCount === 1 ? 3 : 3 - ((rank - 1) / (lowCount - 1)) * 3;
          return {
            ...task,
            urgencyBand: 'low' as const,
            urgencyNumber: Math.round(urgencyNumber * 10) / 10,
            urgencyRank: rank,
            willDoToday: decision.willDoToday ? 'today' : false,
            hasCustomPriority: false,
          };
        }

        return task;
      }));

    setRerankState(null);
    setRerankingBucket(null);
    setIsTournamentModalOpen(false);
  }, [user, rerankState, rerankingBucket]);

  // Helper to get tasks formatted for modals
  const getTasksForBucketing = useCallback(() => {
    const categoryMap = new Map(categories.map(c => [c.id, c.name]));
    return tasks
      .filter(t => !t.completed)
      .map(t => ({
        id: t.id,
        title: t.title,
        categoryName: categoryMap.get(t.categoryId) || 'Uncategorized',
        createdAt: t.createdAt,
        description: t.description,
      }));
  }, [tasks, categories]);

  const getTasksForTournament = useCallback(() => {
    const categoryMap = new Map(categories.map(c => [c.id, c.name]));
    return tasks
      .filter(t => !t.completed)
      .map(t => ({
        id: t.id,
        title: t.title,
        categoryName: categoryMap.get(t.categoryId) || 'Uncategorized',
        description: t.description,
      }));
  }, [tasks, categories]);

  // Calculate task counts for settings modal
  const taskCountsForSettings = useMemo(() => {
    const incompleteTasks = tasks.filter(t => !t.completed);
    return {
      total: incompleteTasks.length,
      high: incompleteTasks.filter(t => t.urgencyBand === 'high').length,
      medium: incompleteTasks.filter(t => t.urgencyBand === 'medium').length,
      low: incompleteTasks.filter(t => t.urgencyBand === 'low').length,
    };
  }, [tasks]);

  // Re-bucket all tasks: show the bucketing flow
  const handleRebucketAll = useCallback(async () => {
    await startRerankFlow();
  }, [startRerankFlow]);

  // Re-rank a specific bucket using tournament
  const handleRerankBucket = useCallback((bucket: 'high' | 'medium' | 'low') => {
    const bucketTasks = tasks.filter(t =>
      !t.completed &&
      (t.urgencyBand === bucket || (!t.urgencyBand && getBandFromNumber(t.urgencyNumber ?? 5) === bucket))
    );

    if (bucketTasks.length < 2) {
      const alert = document.createElement('div');
      alert.className = 'delete-alert';
      alert.textContent = `Need at least 2 tasks in ${bucket} bucket to re-rank`;
      document.body.appendChild(alert);
      setTimeout(() => alert.remove(), 2000);
      return;
    }

    const taskIds = bucketTasks.map(t => t.id);
    const tournamentState = initializeTournament(taskIds);

    // Create a minimal rerank state just for the tournament
    setRerankState({
      bucketingState: null,
      tournamentState,
      bucketSummary: null,
    });

    // Store the bucket being re-ranked for proper urgencyNumber calculation
    setRerankingBucket(bucket);
    setIsTournamentModalOpen(true);
  }, [tasks]);

  // Handle merge-sort comparison choice
  const handleSmartRankingChoice = useCallback(
    async (choice: 'left' | 'right' | 'skip' | 'too-tough') => {
      if (!smartRankingState || !preferenceGraph || !smartRankingCurrentPair) return;

      const { taskA, taskB } = smartRankingCurrentPair;
      let newState: SmartRankingState;

      // Record comparison for 'left' and 'right' choices
      if (choice === 'left' || choice === 'right') {
        const moreUrgentId = choice === 'left' ? taskA : taskB;
        const lessUrgentId = choice === 'left' ? taskB : taskA;

        // Record in Firestore
        await recordComparison(moreUrgentId, lessUrgentId);

        // Process the choice
        newState = processSmartChoice(smartRankingState, taskA, taskB, choice, preferenceGraph);
      } else if (choice === 'too-tough') {
        newState = processSmartTooTough(smartRankingState, taskA, taskB, preferenceGraph);
      } else {
        // skip - record the pair so we don't ask again
        newState = processSmartSkip(smartRankingState, taskA, taskB);
      }

      // Get progress info (includes skipped count)
      const progress = getSmartRankingProgress(newState, preferenceGraph);
      setComparisonsSkipped(progress.comparisonsSkipped);

      // Get next comparison
      const nextResult = getNextSmartComparison(newState, preferenceGraph);

      if (nextResult?.type === 'comparison') {
        setSmartRankingState(nextResult.state);
        setSmartRankingCurrentPair({ taskA: nextResult.taskA, taskB: nextResult.taskB });
      } else if (nextResult?.type === 'complete') {
        // No more comparisons needed - sort is complete
        console.log('handleSmartRankingChoice: ranking complete!');
        await applySmartRankingFinalOrder(nextResult.state.finalOrder);
        setSmartRankingState(nextResult.state);
        setSmartRankingCurrentPair(null);
        setRerankAllComplete(true);
      }
    },
    [smartRankingState, preferenceGraph, smartRankingCurrentPair, recordComparison, applySmartRankingFinalOrder]
  );

  const handleWillDoTodayChange = async (
    taskId: string,
    willDoToday: 'today' | 'pinned' | false
  ) => {
    if (!isFirebaseInitialized || !user) return;

    try {
      const task = tasks.find(t => t.id === taskId);
      // Promoting an unbanded backlog task to Today/Pinned: assign a default
      // band ('medium') and kick off the comparison flow so it lands in the
      // right place in the stack.
      const needsRanking =
        !!task &&
        (willDoToday === 'today' || willDoToday === 'pinned') &&
        !task.urgencyBand &&
        task.urgencyRank == null;

      const updates: Partial<TaskItem> = { willDoToday };
      if (needsRanking) {
        updates.urgencyBand = 'medium';
        updates.urgency = 'medium';
        updates.urgencyNumber = getBandMidpoint('medium');
      }

      await updateDoc(doc(db, `users/${user.uid}/tasks`, taskId), updates);

      setTasks(
        tasks.map(t => (t.id === taskId ? { ...t, ...updates } : t))
      );

      // Update descriptionModalTask if it's the same task
      if (descriptionModalTask && descriptionModalTask.id === taskId) {
        setDescriptionModalTask({
          ...descriptionModalTask,
          ...updates,
        });
      }

      if (needsRanking && task) {
        const promotedTask: TaskItem = { ...task, ...updates };
        startComparisonSession(promotedTask, 'medium');
      }
    } catch (error) {
      console.error('Error updating will do today status:', error);
    }
  };

  const handleTimerToggle = async (taskId: string) => {
    if (!isFirebaseInitialized || !user) return;

    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    try {
      const taskRef = doc(db, `users/${user.uid}/tasks`, taskId);

      if (task.timerStartedAt) {
        // Stop timer - calculate total elapsed time
        const now = Date.now();
        const sessionElapsed = Math.floor((now - task.timerStartedAt) / 1000);
        const totalElapsed = (task.totalElapsedTime || 0) + sessionElapsed;

        // Update per-user time tracking
        const userTimeTracking = task.userTimeTracking || {};
        const currentUserTime = userTimeTracking[user.uid] || 0;
        const updatedUserTimeTracking = {
          ...userTimeTracking,
          [user.uid]: currentUserTime + sessionElapsed,
        };

        // Update local state immediately (optimistic update)
        setTasks(
          tasks.map(t =>
            t.id === taskId
              ? {
                  ...t,
                  timerStartedAt: undefined,
                  totalElapsedTime: totalElapsed,
                  userTimeTracking: updatedUserTimeTracking,
                }
              : t
          )
        );

        // Update descriptionModalTask if it's the same task
        if (descriptionModalTask && descriptionModalTask.id === taskId) {
          setDescriptionModalTask({
            ...descriptionModalTask,
            timerStartedAt: undefined,
            totalElapsedTime: totalElapsed,
            userTimeTracking: updatedUserTimeTracking,
          });
        }

        // Update Firebase in the background
        const updates = {
          timerStartedAt: deleteField(),
          totalElapsedTime: totalElapsed,
          userTimeTracking: updatedUserTimeTracking,
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updateDoc(taskRef, updates as any).catch(error => {
          console.error('Error stopping timer:', error);
        });

        // Sync userTimeTracking for shared tasks (don't await)
        if (task.sharedWith?.length || task.sharedBy) {
          syncSharedTask(taskId, {
            userTimeTracking: updatedUserTimeTracking,
          }).catch(error => {
            console.error('Error syncing shared task timer:', error);
          });
        }
      } else {
        // Start timer
        const now = Date.now();

        // Update local state immediately (optimistic update)
        setTasks(
          tasks.map(t => (t.id === taskId ? { ...t, timerStartedAt: now } : t))
        );

        // Update descriptionModalTask if it's the same task
        if (descriptionModalTask && descriptionModalTask.id === taskId) {
          setDescriptionModalTask({
            ...descriptionModalTask,
            timerStartedAt: now,
          });
        }

        // Update Firebase in the background
        updateDoc(taskRef, {
          timerStartedAt: now,
          totalElapsedTime: task.totalElapsedTime || 0,
        }).catch(error => {
          console.error('Error starting timer:', error);
        });
      }
    } catch (error) {
      console.error('Error toggling timer:', error);
    }
  };

  const handleDescriptionSave = async (title: string, content: string) => {
    if (!descriptionModalTask || !user) return;

    try {
      const taskRef = doc(
        db,
        `users/${user.uid}/tasks`,
        descriptionModalTask.id
      );
      const updates = {
        title,
        description: content,
      };

      await updateDoc(taskRef, updates);

      // Sync if this is a shared task
      if (
        descriptionModalTask.sharedWith?.length ||
        descriptionModalTask.sharedBy
      ) {
        await syncSharedTask(descriptionModalTask.id, updates);
      }

      setTasks(
        tasks.map(task =>
          task.id === descriptionModalTask.id
            ? { ...task, title, description: content }
            : task
        )
      );
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDescriptionClick = (taskId: string) => {
    // Use tasksWithDerivedUrgency to get the correct derived urgencyNumber
    const task = tasksWithDerivedUrgency.find(t => t.id === taskId);
    if (task) {
      setDescriptionModalTask(task);
    }
  };

  const filterTasks = (
    tasksToFilter: TaskItem[],
    categoryId: string | null,
    searchText: string,
    pinnedFilter: boolean,
    todayFilter: boolean,
    completedFilter: boolean,
    timerFilter: boolean,
    personFilter: string | null
  ) => {
    const twelveHoursAgo = Date.now() - 12 * 60 * 60 * 1000; // 12 hours in milliseconds

    return tasksToFilter
      .filter(task => !task.snoozedUntil || task.snoozedUntil <= Date.now())
      .filter(task => {
        // First, filter by category
        if (categoryId === 'all') {
          // Show all tasks
          return true;
        }
        // Filter by specific category
        return task.categoryId === categoryId;
      })
      .filter(task => {
        // Then apply pinned filter if active
        if (pinnedFilter) {
          return (
            task.willDoToday === 'pinned' &&
            (!task.completed ||
              (task.completedAt && task.completedAt > twelveHoursAgo))
          );
        }
        return true;
      })
      .filter(task => {
        // Then apply today filter if active
        if (todayFilter) {
          return (
            task.willDoToday === 'today' &&
            (!task.completed ||
              (task.completedAt && task.completedAt > twelveHoursAgo))
          );
        }
        return true;
      })
      .filter(task => {
        // Then apply completed filter if active
        if (completedFilter) {
          return task.completed;
        }
        return true;
      })
      .filter(task => {
        // Then apply timer filter if active
        if (timerFilter) {
          return (
            task.timerStartedAt !== undefined && task.timerStartedAt !== null
          );
        }
        return true;
      })
      .filter(task => {
        // Then apply person filter if active
        if (personFilter) {
          return task.sharedWith?.some(user => user.userId === personFilter);
        }
        return true;
      })
      .filter(
        task =>
          !searchText ||
          task.title.toLowerCase().includes(searchText.toLowerCase())
      );
  };

  const sortTasks = (tasksToSort: TaskItem[], categoriesList: Category[]) => {
    // Sort by: isDone, priority, categoryOrder, manualOrder (ascending)
    return tasksToSort.sort((a, b) => {
      // If one is completed and the other isn't, uncompleted comes first
      if (!!a.completed !== !!b.completed) {
        return a.completed ? 1 : -1;
      }

      // If both are uncompleted, sort by willDoToday priority: 'today' > 'pinned' > false
      if (!a.completed && !b.completed) {
        const priorityA =
          a.willDoToday === 'today' ? 2 : a.willDoToday === 'pinned' ? 1 : 0;
        const priorityB =
          b.willDoToday === 'today' ? 2 : b.willDoToday === 'pinned' ? 1 : 0;
        if (priorityA !== priorityB) {
          return priorityB - priorityA; // Higher priority first
        }

        // Then sort by urgency (priority)
        // Use ?? instead of || because 0 is a valid urgencyNumber
        const urgencyNumberA =
          a.urgencyNumber ??
          (a.urgency === 'high' ? 8 : a.urgency === 'medium' ? 4 : 0);
        const urgencyNumberB =
          b.urgencyNumber ??
          (b.urgency === 'high' ? 8 : b.urgency === 'medium' ? 4 : 0);
        const urgencyDiff = urgencyNumberB - urgencyNumberA;
        if (urgencyDiff !== 0) return urgencyDiff;

        // If urgency is the same, sort by category order
        const categoryA = categoriesList.find(cat => cat.id === a.categoryId);
        const categoryB = categoriesList.find(cat => cat.id === b.categoryId);

        // If both tasks have categories, sort by their order
        if (categoryA && categoryB) {
          const categoryDiff =
            categoriesList.indexOf(categoryA) -
            categoriesList.indexOf(categoryB);
          if (categoryDiff !== 0) return categoryDiff;

          // If same category, sort by manualOrder (ascending)
          const manualOrderA = a.manualOrder || 0;
          const manualOrderB = b.manualOrder || 0;
          if (manualOrderA !== manualOrderB) {
            return manualOrderA - manualOrderB; // Ascending order
          }
        }

        // If only one task has a category, prioritize the categorized task
        if (categoryA) return -1;
        if (categoryB) return 1;

        // If neither has a category, sort by manualOrder (ascending), then creation date
        const manualOrderA = a.manualOrder || 0;
        const manualOrderB = b.manualOrder || 0;
        if (manualOrderA !== manualOrderB) {
          return manualOrderA - manualOrderB; // Ascending order
        }

        // If manualOrder is the same, sort by creation date (newer first)
        return (b.createdAt || 0) - (a.createdAt || 0);
      }

      // If both are completed, sort by completion time (most recent first)
      return (b.completedAt || 0) - (a.completedAt || 0);
    });
  };

  const tasksWithDerivedUrgency = useMemo(() => {
    // Group tasks by band
    const bandGroups: Record<UrgencyBand, TaskItem[]> = {
      high: [],
      medium: [],
      low: [],
    };

    for (const task of tasks) {
      if (task.completed) continue;
      const band =
        task.urgencyBand || getBandFromNumber(task.urgencyNumber ?? 5);
      bandGroups[band].push(task);
    }

    // Sort each band by rank
    for (const band of Object.keys(bandGroups) as UrgencyBand[]) {
      bandGroups[band].sort(
        (a, b) => (a.urgencyRank ?? 999) - (b.urgencyRank ?? 999)
      );
    }

    // Build position maps for each band (1-indexed position in sorted order)
    const positionMaps: Record<UrgencyBand, Map<string, number>> = {
      high: new Map(),
      medium: new Map(),
      low: new Map(),
    };

    for (const band of Object.keys(bandGroups) as UrgencyBand[]) {
      const rankedTasks = bandGroups[band].filter(t => t.urgencyRank != null);
      rankedTasks.forEach((task, index) => {
        positionMaps[band].set(task.id, index + 1); // 1-indexed position
      });
    }

    // Derive urgency numbers
    return tasks.map(task => {
      if (task.completed) return task;

      // Unranked tasks with a band get max score for their band (put at top)
      if (task.urgencyRank == null && task.urgencyBand) {
        return {
          ...task,
          urgencyNumber: getBandMax(task.urgencyBand),
        };
      }

      // Keep original urgencyNumber for tasks without band or rank
      if (task.urgencyRank == null) return task;

      const band =
        task.urgencyBand || getBandFromNumber(task.urgencyNumber ?? 5);
      const position = positionMaps[band].get(task.id);
      const totalRanked = positionMaps[band].size;

      // Use position in sorted order, not raw urgencyRank
      const derivedNumber = deriveUrgencyNumber(position ?? 1, band, totalRanked);

      return {
        ...task,
        urgencyNumber: Math.round(derivedNumber * 10) / 10,
      };
    });
  }, [tasks]);

  const sortedAndFilteredTasks = useMemo(() => {
    // When heatmap date filter is active, use allTasksForHeatmap and filter by date
    if (heatmapFilterDate) {
      const startOfDay = new Date(heatmapFilterDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(heatmapFilterDate);
      endOfDay.setHours(23, 59, 59, 999);

      const tasksCompletedOnDay = allTasksForHeatmap.filter(task => {
        // Must be completed on the selected day
        const completedOnDay =
          task.completed &&
          task.completedAt &&
          task.completedAt >= startOfDay.getTime() &&
          task.completedAt <= endOfDay.getTime();

        if (!completedOnDay) return false;

        // Apply category filter if not 'all'
        if (selectedCategoryId !== 'all') {
          return task.categoryId === selectedCategoryId;
        }

        return true;
      });

      return sortTasks(tasksCompletedOnDay, categories);
    }

    const filteredTasks = filterTasks(
      tasksWithDerivedUrgency,
      selectedCategoryId,
      searchQuery,
      isPinnedFilterActive,
      isTodayFilterActive,
      isCompletedFilterActive,
      isTimerFilterActive,
      personFilter
    );
    return sortTasks(filteredTasks, categories);
  }, [
    tasksWithDerivedUrgency,
    allTasksForHeatmap,
    heatmapFilterDate,
    selectedCategoryId,
    categories,
    searchQuery,
    isPinnedFilterActive,
    isTodayFilterActive,
    isCompletedFilterActive,
    isTimerFilterActive,
    personFilter,
  ]);

  const snoozedTasks = useMemo(() => {
    const now = Date.now();
    return tasks.filter(task => task.snoozedUntil && task.snoozedUntil > now);
  }, [tasks]);

  const hasActiveTimers = useMemo(() => {
    return tasks.some(
      task => task.timerStartedAt !== undefined && task.timerStartedAt !== null
    );
  }, [tasks]);

  const peopleList = useMemo(() => {
    return aggregatePeopleFromTasks(tasks);
  }, [tasks]);

  const selectedPerson = useMemo((): PersonWithCount | null => {
    return peopleList.find(p => p.userId === personFilter) || null;
  }, [peopleList, personFilter]);

  // Clear timer filter when no active timers exist
  useEffect(() => {
    if (!hasActiveTimers && isTimerFilterActive) {
      setIsTimerFilterActive(false);
      // Update URL to remove timer param
      const params = new URLSearchParams();
      params.set('category', selectedCategoryId);
      if (isPinnedFilterActive) {
        params.set('pinned', 'true');
      }
      if (isTodayFilterActive) {
        params.set('today', 'true');
      }
      if (isCompletedFilterActive) {
        params.set('completed', 'true');
      }
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [
    hasActiveTimers,
    isTimerFilterActive,
    selectedCategoryId,
    isPinnedFilterActive,
    isTodayFilterActive,
    isCompletedFilterActive,
    navigate,
  ]);

  const handleReorderCategories = (reorderedCategories: Category[]) => {
    if (!user) return;

    // Update local state immediately
    setCategories(reorderedCategories);

    // Update Firebase asynchronously
    const batch = writeBatch(db);
    reorderedCategories.forEach(category => {
      const categoryRef = doc(db, `users/${user.uid}/categories`, category.id);
      batch.update(categoryRef, { order: category.order });
    });

    // Fire and forget the database update
    batch.commit().catch(error => {
      console.error('Error updating category order:', error);
      // Optionally, you could add a retry mechanism or show an error notification here
    });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Custom setter for newTaskUrgencyNumber that also saves to database (kept for backward compatibility)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const handleUrgencyNumberChange = async (value: number) => {
    setNewTaskUrgencyNumber(value);

    if (!isFirebaseInitialized || !user) return;

    try {
      const userPrefsRef = doc(db, `users/${user.uid}/preferences`, 'general');
      await setDoc(
        userPrefsRef,
        { newTaskUrgencyNumber: value },
        { merge: true }
      );
    } catch (error) {
      console.error('Error saving urgency number preference:', error);
    }
  };

  // Handler for timezone change
  const handleTimezoneChange = (timezone: string) => {
    setUserTimezone(timezone);
    localStorage.setItem('userTimezone', timezone);
  };

  // Handler for heatmap day click - shows all tasks completed on that day
  const handleHeatmapDayClick = (date: Date) => {
    // Set the filter date
    setHeatmapFilterDate(date);

    // Turn on completed filter, turn off pinned/today/timer filters
    // Keep the current category selection
    setIsCompletedFilterActive(true);
    setIsPinnedFilterActive(false);
    setIsTodayFilterActive(false);
    setIsTimerFilterActive(false);

    // Close the activity panel
    setActiveWidget(null);

    // Update URL - keep current category
    const params = new URLSearchParams();
    params.set('category', selectedCategoryId);
    params.set('completed', 'true');
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Handler to clear heatmap date filter
  const handleClearHeatmapFilter = () => {
    setHeatmapFilterDate(null);
  };

  const handleResumeRerank = useCallback(() => {
    if (rerankQueue.length === 0) return;

    const nextTaskId = rerankQueue[0];
    const nextTask = tasks.find(t => t.id === nextTaskId);
    if (nextTask) {
      setRerankQueue(prev => prev.slice(1));
      const nextBand = nextTask.urgencyBand || getBandFromNumber(nextTask.urgencyNumber ?? 5);
      startComparisonSession(nextTask, nextBand);
    }
  }, [rerankQueue, tasks, startComparisonSession]);

  const handleCancelRerank = useCallback(() => {
    setRerankQueue([]);
  }, []);

  // If user is not authenticated, show login page
  if (!user) {
    return <Login />;
  }

  if (!isFirebaseInitialized) {
    return <div className="app">Loading...</div>;
  }

  return (
    <MantineProvider withGlobalStyles withNormalizeCSS>
      <div className="app">
        {/* Resume alert for interrupted re-rank all (sequential mode) */}
        {rerankQueue.length > 0 && !isComparisonModalOpen && (
          <div className="rerank-resume-alert">
            <span>
              Re-rank paused — {rerankQueue.length} task{rerankQueue.length !== 1 ? 's' : ''} remaining
            </span>
            <div className="rerank-resume-actions">
              <button onClick={handleResumeRerank} className="rerank-resume-button">
                Resume
              </button>
              <button onClick={handleCancelRerank} className="rerank-cancel-button">
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* Resume alert for interrupted re-rank all (merge-sort mode) */}
        {smartRankingState && !smartRankingState.isComplete && !isComparisonModalOpen && tasks.length > 0 && (
          <div className="rerank-resume-alert">
            <span>
              Re-rank paused — {Math.max(0, smartRankingState.estimatedTotal - smartRankingState.comparisonsMade - smartRankingState.comparisonsSkipped)} comparisons remaining
            </span>
            <div className="rerank-resume-actions">
              <button
                onClick={() => {
                  // Rebuild preference graph from tasks if needed
                  let graph = preferenceGraph;
                  if (!graph) {
                    const allIncompleteTasks = tasks.filter(t => !t.completed);
                    graph = buildPreferenceGraph(allIncompleteTasks);
                    computeTransitiveClosure(graph);
                    setPreferenceGraph(graph);
                  }

                  // If we have a current pair, just open the modal
                  if (smartRankingCurrentPair) {
                    setIsComparisonModalOpen(true);
                  } else {
                    // Need to get the next comparison
                    const nextResult = getNextSmartComparison(smartRankingState, graph);
                    if (nextResult?.type === 'comparison') {
                      setSmartRankingState(nextResult.state);
                      setSmartRankingCurrentPair({ taskA: nextResult.taskA, taskB: nextResult.taskB });
                      setIsComparisonModalOpen(true);
                    } else if (nextResult?.type === 'complete') {
                      // Sort is already complete from transitivity
                      applySmartRankingFinalOrder(nextResult.state.finalOrder);
                      setSmartRankingState(nextResult.state);
                      setRerankAllComplete(true);
                      setIsComparisonModalOpen(true);
                    }
                  }
                }}
                className="rerank-resume-button"
              >
                Resume
              </button>
              <button
                onClick={() => {
                  setSmartRankingState(null);
                  setSmartRankingCurrentPair(null);
                  setRerankAllTotal(0);
                  setRerankAllComplete(false);
                  setPreferenceGraph(null);
                  setComparisonsSkipped(0);
                }}
                className="rerank-cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* Resume alert for interrupted bucketing/tournament flow */}
        {rerankState && !isBucketingModalOpen && !isTournamentModalOpen && tasks.length > 0 && (
          <div className="rerank-resume-alert">
            <span>
              {rerankState.tournamentState && !isTournamentComplete(rerankState.tournamentState)
                ? `Tournament paused — ${rerankState.tournamentState.tasks.length - rerankState.tournamentState.history.length} comparisons remaining`
                : rerankState.bucketingState && !isBucketingComplete(rerankState.bucketingState)
                ? `Bucketing paused — ${rerankState.bucketingState.allTasks.length - rerankState.bucketingState.currentIndex} tasks remaining`
                : `Bucketing complete — ready to start tournament`}
            </span>
            <div className="rerank-resume-actions">
              <button
                onClick={() => {
                  if (rerankState.tournamentState && !isTournamentComplete(rerankState.tournamentState)) {
                    setIsTournamentModalOpen(true);
                  } else {
                    setIsBucketingModalOpen(true);
                  }
                }}
                className="rerank-resume-button"
              >
                Resume
              </button>
              <button
                onClick={() => {
                  setRerankState(null);
                }}
                className="rerank-cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        <div className="app-layout">
          {user && (
            <Sidebar
              activeWidget={activeWidget}
              onWidgetToggle={(widget) => setActiveWidget(widget || null)}
              isMobile={isMobile}
              hasPanelOpen={(activeWidget === 'notepad' || activeWidget === 'settings' || activeWidget === 'activity' || activeWidget === 'people') && !useModalForNotepad}
              onSettingsClick={() => setActiveWidget(activeWidget === 'settings' ? null : 'settings')}
              isSettingsActive={activeWidget === 'settings'}
              onActivityClick={() => setActiveWidget(activeWidget === 'activity' ? null : 'activity')}
              isActivityActive={activeWidget === 'activity'}
              onPeopleClick={() => setActiveWidget(activeWidget === 'people' ? null : 'people')}
              isPeopleActive={activeWidget === 'people'}
            />
          )}
          {activeWidget === 'notepad' && !useModalForNotepad && user && (
            <NotepadPanel
              content={notepadContent}
              onContentChange={handleNotepadChange}
              onClose={() => setActiveWidget(null)}
              width={panelWidth}
              onWidthChange={handlePanelWidthChange}
            />
          )}
          {activeWidget === 'settings' && !useModalForNotepad && user && (
            <SettingsPanel
              onClose={() => setActiveWidget(null)}
              userTimezone={userTimezone}
              onTimezoneChange={handleTimezoneChange}
              userId={user.uid}
              onRebucketAll={handleRebucketAll}
              onRerankBucket={handleRerankBucket}
              taskCounts={taskCountsForSettings}
              onLogout={handleLogout}
              width={panelWidth}
              onWidthChange={handlePanelWidthChange}
            />
          )}
          {activeWidget === 'activity' && !useModalForNotepad && user && (
            <ActivityPanel
              tasks={
                selectedCategoryId === 'all'
                  ? allTasksForHeatmap
                  : allTasksForHeatmap.filter(
                      task => task.categoryId === selectedCategoryId
                    )
              }
              onClose={() => setActiveWidget(null)}
              onDayClick={handleHeatmapDayClick}
              width={panelWidth}
              onWidthChange={handlePanelWidthChange}
            />
          )}
          {activeWidget === 'people' && !useModalForNotepad && (
            <PeoplePanel
              people={peopleList}
              selectedPersonId={personFilter}
              onPersonSelect={setPersonFilter}
              onClose={() => setActiveWidget(null)}
              width={panelWidth}
              onWidthChange={handlePanelWidthChange}
            />
          )}
          <div
            className="task-container"
            style={(activeWidget === 'notepad' || activeWidget === 'settings' || activeWidget === 'activity' || activeWidget === 'people') && !useModalForNotepad ? { marginLeft: `${64 + panelWidth}px` } : undefined}
          >
          <div className="category-pills">
            <button
              className={`category-pill non-draggable ${isPinnedFilterActive ? 'active' : ''}`}
              onClick={() => {
                const newPinnedState = !isPinnedFilterActive;
                setIsPinnedFilterActive(newPinnedState);
                // If turning on pinned, turn off today (mutually exclusive)
                if (newPinnedState) {
                  setIsTodayFilterActive(false);
                }
                // Update URL with new filter state
                const params = new URLSearchParams();
                params.set('category', selectedCategoryId);
                if (newPinnedState) {
                  params.set('pinned', 'true');
                }
                if (isCompletedFilterActive) {
                  params.set('completed', 'true');
                }
                if (isTimerFilterActive) {
                  params.set('timer', 'true');
                }
                navigate(`?${params.toString()}`, { replace: true });
              }}
            >
              📌 Pinned
            </button>
            <button
              className={`category-pill non-draggable ${isTodayFilterActive ? 'active' : ''}`}
              onClick={() => {
                const newTodayState = !isTodayFilterActive;
                setIsTodayFilterActive(newTodayState);
                // If turning on today, turn off pinned (mutually exclusive)
                if (newTodayState) {
                  setIsPinnedFilterActive(false);
                }
                // Update URL with new filter state
                const params = new URLSearchParams();
                params.set('category', selectedCategoryId);
                if (newTodayState) {
                  params.set('today', 'true');
                }
                if (isCompletedFilterActive) {
                  params.set('completed', 'true');
                }
                if (isTimerFilterActive) {
                  params.set('timer', 'true');
                }
                navigate(`?${params.toString()}`, { replace: true });
              }}
            >
              🕐 Today
            </button>
            <button
              className={`category-pill non-draggable ${isCompletedFilterActive ? 'active' : ''}`}
              onClick={() => {
                const newCompletedState = !isCompletedFilterActive;
                setIsCompletedFilterActive(newCompletedState);
                // Update URL with new filter state
                const params = new URLSearchParams();
                params.set('category', selectedCategoryId);
                if (isPinnedFilterActive) {
                  params.set('pinned', 'true');
                }
                if (isTodayFilterActive) {
                  params.set('today', 'true');
                }
                if (newCompletedState) {
                  params.set('completed', 'true');
                }
                if (isTimerFilterActive) {
                  params.set('timer', 'true');
                }
                navigate(`?${params.toString()}`, { replace: true });
              }}
            >
              ✅ Completed
            </button>
            {hasActiveTimers && (
              <button
                className={`category-pill non-draggable ${isTimerFilterActive ? 'active' : ''}`}
                onClick={() => {
                  const newTimerState = !isTimerFilterActive;
                  setIsTimerFilterActive(newTimerState);
                  // Update URL with new filter state
                  const params = new URLSearchParams();
                  params.set('category', selectedCategoryId);
                  if (isPinnedFilterActive) {
                    params.set('pinned', 'true');
                  }
                  if (isTodayFilterActive) {
                    params.set('today', 'true');
                  }
                  if (isCompletedFilterActive) {
                    params.set('completed', 'true');
                  }
                  if (newTimerState) {
                    params.set('timer', 'true');
                  }
                  navigate(`?${params.toString()}`, { replace: true });
                }}
              >
                ⏳
              </button>
            )}
            {personFilter && selectedPerson && (
              <div className="person-filter-chip">
                <span>{selectedPerson.email}</span>
                <button
                  className="person-filter-chip-close"
                  onClick={() => setPersonFilter(null)}
                  aria-label="Clear person filter"
                >
                  ×
                </button>
              </div>
            )}
            <div className="category-pill-separator" />
            <button
              className={`category-pill non-draggable ${selectedCategoryId === 'all' ? 'active' : ''}`}
              onClick={() => {
                setSelectedCategoryId('all');
                // Preserve filter state in URL
                const params = new URLSearchParams();
                params.set('category', 'all');
                if (isPinnedFilterActive) {
                  params.set('pinned', 'true');
                }
                if (isTodayFilterActive) {
                  params.set('today', 'true');
                }
                if (isCompletedFilterActive) {
                  params.set('completed', 'true');
                }
                if (isTimerFilterActive) {
                  params.set('timer', 'true');
                }
                navigate(`?${params.toString()}`, { replace: true });
              }}
            >
              All
            </button>
            {(isDragging ? tempCategories : categories).map(category => (
              <button
                key={category.id}
                className={`category-pill ${selectedCategoryId === category.id ? 'active' : ''} ${draggedCategory?.id === category.id && isDragging ? 'dragging' : ''}`}
                onClick={() => {
                  // Toggle: if already selected, go back to 'all'
                  const newCategoryId =
                    selectedCategoryId === category.id ? 'all' : category.id;
                  setSelectedCategoryId(newCategoryId);
                  // Preserve filter state in URL
                  const params = new URLSearchParams();
                  params.set('category', newCategoryId);
                  if (isPinnedFilterActive) {
                    params.set('pinned', 'true');
                  }
                  if (isTodayFilterActive) {
                    params.set('today', 'true');
                  }
                  if (isCompletedFilterActive) {
                    params.set('completed', 'true');
                  }
                  if (isTimerFilterActive) {
                    params.set('timer', 'true');
                  }
                  navigate(`?${params.toString()}`, { replace: true });
                }}
                draggable
                onDragStart={e => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', category.id);
                  setIsDragging(true);
                  setDraggedCategory(category);
                  setTempCategories(categories);
                }}
                onDragEnd={() => {
                  if (draggedCategory && tempCategories.length > 0) {
                    handleReorderCategories(tempCategories);
                  }
                  setIsDragging(false);
                  setDraggedCategory(null);
                  setTempCategories([]);
                }}
                onDragOver={e => {
                  e.preventDefault();
                  if (draggedCategory && draggedCategory.id !== category.id) {
                    const draggedIndex = categories.findIndex(
                      c => c.id === draggedCategory.id
                    );
                    const targetIndex = categories.findIndex(
                      c => c.id === category.id
                    );

                    if (draggedIndex !== -1 && targetIndex !== -1) {
                      const newCategories = [...categories];
                      const [removed] = newCategories.splice(draggedIndex, 1);
                      newCategories.splice(targetIndex, 0, removed);

                      // Update order property for all categories
                      const updatedCategories = newCategories.map(
                        (cat, index) => ({
                          ...cat,
                          order: index,
                        })
                      );

                      setTempCategories(updatedCategories);
                    }
                  }
                }}
                onDrop={e => {
                  e.preventDefault();
                  if (!draggedCategory) return;

                  // Use the final tempCategories if available, otherwise calculate the new order
                  const finalCategories =
                    tempCategories.length > 0
                      ? tempCategories
                      : (() => {
                          const draggedIndex = categories.findIndex(
                            c => c.id === draggedCategory.id
                          );
                          const targetIndex = categories.findIndex(
                            c => c.id === category.id
                          );

                          if (draggedIndex === -1 || targetIndex === -1)
                            return categories;

                          const newCategories = [...categories];
                          const [removed] = newCategories.splice(
                            draggedIndex,
                            1
                          );
                          newCategories.splice(targetIndex, 0, removed);

                          return newCategories.map((cat, index) => ({
                            ...cat,
                            order: index,
                          }));
                        })();

                  // Update both local state and Firebase
                  setCategories(finalCategories);
                  handleReorderCategories(finalCategories);

                  // Reset drag state
                  setIsDragging(false);
                  setDraggedCategory(null);
                  setTempCategories([]);
                }}
              >
                {category.name}
              </button>
            ))}
            <button
              className={`category-pill settings non-draggable ${isCategoryModalOpen ? 'active' : ''}`}
              onClick={() => setIsCategoryModalOpen(true)}
              title="Edit categories"
            >
              📝
            </button>
          </div>
          <form
            className="new-task-form"
            onSubmit={e => {
              // Only submit if we're not in the category select dropdown
              if (
                !document.querySelector(
                  '.category-select-dropdown:focus-within'
                )
              ) {
                handleAddTask(e);
              } else {
                e.preventDefault();
              }
            }}
          >
            <div className="task-input-container">
              <button
                type="button"
                className={`search-toggle-btn ${isSearchMode ? 'active' : ''}`}
                onClick={() => {
                  if (isSearchMode) {
                    setSearchQuery('');
                  }
                  setIsSearchMode(!isSearchMode);
                }}
                ref={searchButtonRef}
              >
                <TbSearch size={18} />
              </button>
              <textarea
                className="new-task-input"
                value={isSearchMode ? searchQuery : newTaskTitle}
                onChange={e => {
                  if (isSearchMode) {
                    setSearchQuery(e.target.value);
                  } else {
                    setNewTaskTitle(e.target.value);
                  }
                  adjustTextareaHeight();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!isSearchMode && newTaskTitle.trim() && !isAddingTask) {
                      handleAddTask(e);
                    }
                  }
                }}
                placeholder={
                  isSearchMode ? 'Search tasks...' : 'Add a new task...'
                }
                ref={inputRef}
                rows={1}
              />
            </div>
            {!isSearchMode && (
              <>
                <div className="task-form-row">
                  <CategorySelect
                    categories={categories}
                    value={newTaskCategoryId}
                    onChange={setNewTaskCategoryId}
                    onCreateCategory={handleAddCategory}
                  />
                  <PriorityBandSelector
                    value={newTaskBand}
                    onChange={setNewTaskBand}
                  />
                  {/* <div className="urgency-pills">
                    <button
                      type="button"
                      className={`urgency-pill ${newTaskUrgency === 'high' ? 'active' : ''}`}
                      onClick={() => setNewTaskUrgency('high')}
                      data-urgency="high"
                    >
                      ❗️ High
                    </button>
                    <button
                      type="button"
                      className={`urgency-pill ${newTaskUrgency === 'medium' ? 'active' : ''}`}
                      onClick={() => setNewTaskUrgency('medium')}
                      data-urgency="medium"
                    >
                      ⚠️ Medium
                    </button>
                    <button
                      type="button"
                      className={`urgency-pill ${newTaskUrgency === 'low' ? 'active' : ''}`}
                      onClick={() => setNewTaskUrgency('low')}
                      data-urgency="low"
                    >
                      ℹ️ Low
                    </button> 
                  </div> */}
                </div>
                <div className="task-form-actions">
                  <button
                    type="submit"
                    className="add-task-btn"
                    disabled={!newTaskTitle.trim() || isAddingTask}
                  >
                    {isAddingTask ? <Loader size="xs" color="white" /> : 'Add'}
                  </button>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      width: '100%',
                      marginTop: 4,
                    }}
                  >
                    <div
                      style={{ height: 30, width: 90 }}
                      className="will-do-switcher-container new-task-will-do-switcher"
                      onMouseMove={e => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const width = rect.width;
                        const section = Math.floor((x / width) * 3);

                        let tooltip = '';
                        if (section === 0) tooltip = 'Pinned';
                        else if (section === 1) tooltip = 'Today';
                        else tooltip = 'None';

                        setNewTaskSwitcherTooltip(tooltip);
                        setNewTaskSwitcherTooltipPosition({
                          x: e.clientX,
                          y: rect.top,
                        });
                      }}
                      onMouseLeave={() => {
                        setNewTaskSwitcherTooltip(null);
                        setNewTaskSwitcherTooltipPosition(null);
                      }}
                    >
                      <SwitchSelector
                        name="new-task-will-do-switcher"
                        onChange={(value: unknown) => {
                          const strValue = value as string;
                          const mappedValue =
                            strValue === 'none'
                              ? false
                              : (strValue as 'pinned' | 'today');
                          setNewTaskWillDoToday(mappedValue);
                        }}
                        options={[
                          {
                            label: (
                              <TbPin
                                size={18}
                                color={
                                  newTaskWillDoToday === 'pinned'
                                    ? '#1976d2'
                                    : '#495057'
                                }
                              />
                            ),
                            value: 'pinned',
                            selectedBackgroundColor: '#e3f2fd',
                          },
                          {
                            label: (
                              <TbClockDown
                                size={18}
                                color={
                                  newTaskWillDoToday === 'today'
                                    ? '#1976d2'
                                    : '#495057'
                                }
                              />
                            ),
                            value: 'today',
                            selectedBackgroundColor: '#e3f2fd',
                          },
                          {
                            label: (
                              <TbX
                                size={18}
                                color={
                                  newTaskWillDoToday === false
                                    ? '#1976d2'
                                    : '#495057'
                                }
                              />
                            ),
                            value: 'none',
                            selectedBackgroundColor: '#e3f2fd',
                          },
                        ]}
                        forcedSelectedIndex={
                          newTaskWillDoToday === 'pinned'
                            ? 0
                            : newTaskWillDoToday === 'today'
                              ? 1
                              : 2
                        }
                        backgroundColor={'#f1f3f5'}
                        fontColor={'#495057'}
                        selectedFontColor={'#1976d2'}
                        fontSize={16}
                        optionBorderRadius={20}
                        wrapperBorderRadius={20}
                      />
                    </div>
                    {newTaskSwitcherTooltip && newTaskSwitcherTooltipPosition && (
                      <div
                        className="switcher-tooltip-floating"
                        style={{
                          left: newTaskSwitcherTooltipPosition.x,
                          top: newTaskSwitcherTooltipPosition.y,
                        }}
                      >
                        {newTaskSwitcherTooltip}
                      </div>
                    )}
                    <button
                      type="button"
                      className="view-snoozed-btn"
                      onClick={() => setIsSnoozedTasksModalOpen(true)}
                    >
                      <span
                        style={{ verticalAlign: '-webkit-baseline-middle' }}
                      >
                        View Snoozed ({snoozedTasks.length})
                      </span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </form>
          <div className="task-progress">
            {(() => {
              const visibleTasks = sortedAndFilteredTasks;
              const completedTasks = visibleTasks.filter(
                task => task.completed
              );
              const progress =
                visibleTasks.length > 0
                  ? Math.round(
                      (completedTasks.length / visibleTasks.length) * 100
                    )
                  : 0;

              return (
                <Group position="apart" align="center" spacing="xs">
                  <div
                    style={{
                      position: 'relative',
                      flex: 1,
                    }}
                  >
                    <Progress
                      value={progress}
                      label={
                        progress === 0 ? undefined : `${progress}% done ✨`
                      }
                      size="xl"
                      radius="xl"
                      style={{ flex: 1, height: '20px' }}
                      color="teal"
                      styles={{
                        root: {
                          backgroundColor: '#e7e7e7',
                          position: 'relative',
                        },
                        label: { color: 'black' },
                      }}
                      data-progress={progress}
                    />
                    <style>
                      {`
                        .mantine-Progress-root[data-progress="0"]::after {
                          content: "0% done";
                          position: absolute;
                          top: 50%;
                          left: 50%;
                          transform: translate(-50%, -50%);
                          color: black;
                          font-size: 10.4px;
                          font-weight: 700;
                        }
                      `}
                    </style>
                  </div>
                </Group>
              );
            })()}
          </div>
          {isTodayFilterActive && !heatmapFilterDate && (
            <div className="today-expiration-callout">
              <div className="callout-content">
                <span className="callout-icon">⏰</span>
                <span className="callout-text">
                  {todayExtendedUntil
                    ? 'Tasks marked as "Today" will stay in the Today column indefinitely.'
                    : 'Tasks marked as "Today" will expire at midnight tonight. Uncompleted tasks will be moved to the pinned section.'}
                </span>
              </div>
              <hr className="callout-divider" />
              <div className="dont-expire-toggle">
                <span className="dont-expire-label">Don't expire tasks</span>
                <Switch
                  checked={!!todayExtendedUntil}
                  onChange={handleToggleDontExpireTasks}
                  size="xs"
                  color="dark"
                  styles={{
                    track: {
                      backgroundColor: todayExtendedUntil ? undefined : '#c4a882',
                      borderColor: todayExtendedUntil ? undefined : '#b8996e',
                    },
                  }}
                />
              </div>
            </div>
          )}
          {heatmapFilterDate && (
            <div className="heatmap-date-filter-callout">
              <div className="callout-content">
                <span className="callout-icon">📊</span>
                <span className="callout-text">
                  Showing all tasks completed on {moment(heatmapFilterDate).format('MMMM D, YYYY')}
                </span>
              </div>
              <button
                className="clear-heatmap-filter-btn"
                onClick={handleClearHeatmapFilter}
              >
                Clear filter
              </button>
            </div>
          )}
          <div className="task-stack">
            {sortedAndFilteredTasks.length > 0 ? (
              sortedAndFilteredTasks.map((task: TaskItem, index: number) => {
                const allTasksInCategory = filterAllTasksInCategory(task);
                const isFirstInCategory =
                  allTasksInCategory.findIndex(t => t.id === task.id) === 0;
                const isLastInCategory =
                  allTasksInCategory.findIndex(t => t.id === task.id) ===
                  allTasksInCategory.length - 1;

                return (
                  <div
                    key={task.id}
                    className="task-wrapper"
                    style={
                      {
                        '--index': index,
                        '--z-index': sortedAndFilteredTasks.length - index,
                      } as React.CSSProperties
                    }
                  >
                    <Task
                      id={task.id}
                      title={task.title}
                      isCompleted={task.completed}
                      category={
                        categories.find(c => c.id === task.categoryId)?.name ||
                        'Uncategorized'
                      }
                      selectedCategoryId={selectedCategoryId}
                      categories={categories}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                      onEdit={handleEdit}
                      onCategoryChange={handleCategoryChange}
                      onCreateCategory={handleAddCategory}
                      urgency={task.urgency}
                      urgencyNumber={task.urgencyNumber}
                      urgencyRank={task.urgencyRank}
                      hasCustomPriority={task.hasCustomPriority}
                      isUnranked={task.urgencyRank == null && !task.hasCustomPriority}
                      onUrgencyChange={handleUrgencyChange}
                      willDoToday={task.willDoToday}
                      onWillDoTodayChange={handleWillDoTodayChange}
                      description={task.description}
                      onDescriptionClick={handleDescriptionClick}
                      onSnooze={handleSnooze}
                      onManualOrderChange={handleManualOrderChange}
                      isFirstInCategory={isFirstInCategory}
                      isLastInCategory={isLastInCategory}
                      timerStartedAt={task.timerStartedAt}
                      totalElapsedTime={task.totalElapsedTime}
                      onTimerToggle={handleTimerToggle}
                      source={task.source}
                      sharedWith={task.sharedWith}
                      sharedBy={task.sharedBy}
                      onRerank={handleRerank}
                      recurrenceDays={task.recurrenceDays}
                    />
                  </div>
                );
              })
            ) : (
              <div
                className="no-tasks-message"
                data-category={selectedCategoryId}
              >
                {isPinnedFilterActive ? (
                  <>
                    What good shall I do this day?{' '}
                    <span>- Benjamin Franklin</span>
                  </>
                ) : isTodayFilterActive ? (
                  'No tasks for today'
                ) : selectedCategoryId === 'all' ? (
                  'No tasks yet'
                ) : selectedCategoryId ? (
                  `No tasks in "${categories.find(c => c.id === selectedCategoryId)?.name || 'Uncategorized'}" category`
                ) : (
                  'No tasks yet'
                )}
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Modal notepad for mobile/tablet */}
        {activeWidget === 'notepad' && useModalForNotepad && user && (
          <NotepadPanel
            content={notepadContent}
            onContentChange={handleNotepadChange}
            onClose={() => setActiveWidget(null)}
            isModal
          />
        )}

        {/* Modal settings for mobile/tablet */}
        {activeWidget === 'settings' && useModalForNotepad && user && (
          <SettingsPanel
            onClose={() => setActiveWidget(null)}
            isModal
            userTimezone={userTimezone}
            onTimezoneChange={handleTimezoneChange}
            userId={user.uid}
            onRebucketAll={handleRebucketAll}
            onRerankBucket={handleRerankBucket}
            taskCounts={taskCountsForSettings}
            onLogout={handleLogout}
          />
        )}

        {/* Modal activity for mobile/tablet */}
        {activeWidget === 'activity' && useModalForNotepad && user && (
          <ActivityPanel
            tasks={
              selectedCategoryId === 'all'
                ? allTasksForHeatmap
                : allTasksForHeatmap.filter(
                    task => task.categoryId === selectedCategoryId
                  )
            }
            onClose={() => setActiveWidget(null)}
            onDayClick={handleHeatmapDayClick}
            isModal
          />
        )}

        {/* Modal people for mobile/tablet */}
        {useModalForNotepad && activeWidget === 'people' && (
          <PeoplePanel
            people={peopleList}
            selectedPersonId={personFilter}
            onPersonSelect={setPersonFilter}
            onClose={() => setActiveWidget(null)}
            isModal
          />
        )}

        <DeleteModal
          isOpen={deleteModalTask !== null}
          taskTitle={deleteModalTask?.title || ''}
          isSharedTask={deleteModalTask?.sharedBy !== undefined}
          sharedWithCount={deleteModalTask?.sharedWith?.length || 0}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
        <CategoryModal
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          categories={categories}
          archivedCategories={archivedCategories}
          onEditCategory={handleEditCategory}
          onDeleteCategory={handleDeleteCategory}
          onArchiveCategory={handleArchiveCategory}
          onUnarchiveCategory={handleUnarchiveCategory}
          onReorderCategories={handleReorderCategories}
        />
        <SnoozedTasksModal
          isOpen={isSnoozedTasksModalOpen}
          onClose={() => setIsSnoozedTasksModalOpen(false)}
          tasks={snoozedTasks}
          categories={categories}
          onUnsnooze={handleUnsnooze}
        />
        <UserSettingsModal
          isOpen={isUserSettingsModalOpen}
          onClose={() => setIsUserSettingsModalOpen(false)}
          userTimezone={userTimezone}
          onTimezoneChange={handleTimezoneChange}
          userId={user?.uid || ''}
          onRebucketAll={handleRebucketAll}
          onRerankBucket={handleRerankBucket}
          taskCounts={taskCountsForSettings}
        />
        <TaskDescriptionModal
          isOpen={descriptionModalTask !== null}
          onClose={() => setDescriptionModalTask(null)}
          taskTitle={descriptionModalTask?.title || ''}
          initialContent={descriptionModalTask?.description}
          createdAt={descriptionModalTask?.createdAt}
          taskId={descriptionModalTask?.id}
          isCompleted={descriptionModalTask?.completed || false}
          timerStartedAt={descriptionModalTask?.timerStartedAt}
          totalElapsedTime={descriptionModalTask?.totalElapsedTime}
          willDoToday={descriptionModalTask?.willDoToday || false}
          sharedWith={descriptionModalTask?.sharedWith}
          sharedBy={descriptionModalTask?.sharedBy}
          currentUserId={user?.uid}
          onToggle={handleToggle}
          onTimerToggle={handleTimerToggle}
          onWillDoTodayChange={handleWillDoTodayChange}
          onSave={handleDescriptionSave}
          onTitleChange={newTitle => {
            if (descriptionModalTask) {
              const updatedTasks = tasks.map(task =>
                task.id === descriptionModalTask.id
                  ? { ...task, title: newTitle }
                  : task
              );
              setTasks(updatedTasks);
            }
          }}
          onShareSuccess={async (email, userId, photoURL) => {
            if (!descriptionModalTask || !user) return;

            try {
              const taskRef = doc(
                db,
                `users/${user.uid}/tasks`,
                descriptionModalTask.id
              );
              const currentSharedWith = descriptionModalTask.sharedWith || [];
              const updatedSharedWith = [
                ...currentSharedWith,
                { email, userId, ...(photoURL ? { photoURL } : {}) },
              ];

              // Also maintain a simple array of user IDs for security rules
              const sharedWithUserIds = updatedSharedWith.map(u => u.userId);

              await updateDoc(taskRef, {
                sharedWith: updatedSharedWith,
                sharedWithUserIds: sharedWithUserIds,
              });

              // Update local state
              const updatedTask = {
                ...descriptionModalTask,
                sharedWith: updatedSharedWith,
              };

              setTasks(
                tasks.map(task =>
                  task.id === descriptionModalTask.id ? updatedTask : task
                )
              );
              setDescriptionModalTask(updatedTask);

              // Show success toast
              const alert = document.createElement('div');
              alert.className = 'share-alert';
              alert.textContent = `Task shared with ${email}`;
              document.body.appendChild(alert);
              setTimeout(() => alert.remove(), 2000);
            } catch (error) {
              console.error('Error updating shared task:', error);
            }
          }}
          onRemoveSharedUser={async (userIdToRemove, email) => {
            if (!descriptionModalTask || !user) return;

            try {
              const response = await fetch('/api/remove-shared-user', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  taskId: descriptionModalTask.id,
                  ownerUserId: user.uid,
                  userIdToRemove: userIdToRemove,
                }),
              });

              const data = await response.json();

              if (data.success) {
                // Update local state
                const taskRef = doc(
                  db,
                  `users/${user.uid}/tasks`,
                  descriptionModalTask.id
                );
                const currentSharedWith = descriptionModalTask.sharedWith || [];
                const updatedSharedWith = currentSharedWith.filter(
                  u => u.userId !== userIdToRemove
                );

                await updateDoc(taskRef, {
                  sharedWith: updatedSharedWith,
                  sharedWithUserIds: updatedSharedWith.map(u => u.userId),
                });

                const updatedTask = {
                  ...descriptionModalTask,
                  sharedWith: updatedSharedWith,
                };

                setTasks(
                  tasks.map(task =>
                    task.id === descriptionModalTask.id ? updatedTask : task
                  )
                );
                setDescriptionModalTask(updatedTask);

                // Show success toast
                const alert = document.createElement('div');
                alert.className = 'share-alert';
                alert.textContent = `Removed ${email} from this task`;
                document.body.appendChild(alert);
                setTimeout(() => alert.remove(), 2000);
              }
            } catch (error) {
              console.error('Error removing shared user:', error);
            }
          }}
          urgency={descriptionModalTask?.urgency}
          urgencyNumber={descriptionModalTask?.urgencyNumber}
          onUrgencyChange={handleUrgencyChange}
          onSnooze={handleSnooze}
          onDelete={handleDelete}
          userTimeTracking={descriptionModalTask?.userTimeTracking}
          recurrenceDays={descriptionModalTask?.recurrenceDays}
          completionLog={descriptionModalTask?.completionLog}
          onRecurrenceChange={handleRecurrenceChange}
          onCompletionLogChange={handleCompletionLogChange}
        />
        <ComparisonScopeModal
          isOpen={isComparisonScopeModalOpen}
          categoryName={categories.find(c => c.id === selectedCategoryId)?.name || 'this category'}
          onChooseCategory={handleComparisonScopeCategory}
          onChooseAll={handleComparisonScopeAll}
          onCancel={handleComparisonScopeCancel}
        />
        <ComparisonModal
          opened={isComparisonModalOpen}
          onClose={() => {
            // Auto-open task modal after celebration so user can add details
            if (celebrationData?.taskId && rerankAllTotal === 0) {
              // Use the base task but with the celebration score for consistency
              const task = tasksWithDerivedUrgency.find(t => t.id === celebrationData.taskId);
              if (task) {
                // Override urgencyNumber with the celebration score for consistency
                setDescriptionModalTask({
                  ...task,
                  urgencyNumber: celebrationData.score,
                });
              }
            }

            setIsComparisonModalOpen(false);
            setCelebrationData(null);

            // Only fully reset state if ranking is complete
            // Otherwise, keep state for resume functionality
            if (rerankAllComplete || (!smartRankingState && rerankQueue.length === 0)) {
              setRerankAllTotal(0);
              setRerankAllCompleted(0);
              setRerankAllComplete(false);
              setRerankQueue([]);
              setPreferenceGraph(null);
              setComparisonsSkipped(0);
              setSmartRankingState(null);
              setSmartRankingCurrentPair(null);
            }
          }}
          rerankAllMode={rerankAllTotal > 0}
          rerankAllTotal={rerankAllTotal}
          rerankAllCompleted={rerankAllCompleted}
          rerankAllComplete={rerankAllComplete}
          comparisonsSkipped={comparisonsSkipped}
          session={comparisonSession}
          newTask={
            pendingNewTask
              ? {
                  id: pendingNewTask.id,
                  title: pendingNewTask.title,
                  categoryName:
                    categories.find(c => c.id === pendingNewTask.categoryId)?.name ||
                    'Uncategorized',
                  createdAt: pendingNewTask.createdAt,
                }
              : null
          }
          candidateTasks={
            comparisonSession
              ? comparisonSession.candidates.map(id => {
                  const task = tasks.find(t => t.id === id);
                  return {
                    id,
                    title: task?.title || '',
                    categoryName:
                      categories.find(c => c.id === task?.categoryId)?.name ||
                      'Uncategorized',
                    createdAt: task?.createdAt || 0,
                  };
                })
              : []
          }
          onChoice={smartRankingState ? handleSmartRankingChoice : handleComparisonChoice}
          onUndo={handleComparisonUndo}
          canUndo={comparisonSession ? comparisonSession.history.length > 0 : false}
          comparisonNumber={comparisonNumber}
          celebration={celebrationData}
          onMarkDone={handleMarkDoneFromComparison}
          // Merge-sort mode props
          mergeSortMode={!!smartRankingState}
          mergeSortLeftTask={
            smartRankingCurrentPair
              ? (() => {
                  const task = tasks.find(t => t.id === smartRankingCurrentPair.taskA);
                  return task
                    ? {
                        id: task.id,
                        title: task.title,
                        categoryName:
                          categories.find(c => c.id === task.categoryId)?.name ||
                          'Uncategorized',
                        createdAt: task.createdAt,
                      }
                    : null;
                })()
              : null
          }
          mergeSortRightTask={
            smartRankingCurrentPair
              ? (() => {
                  const task = tasks.find(t => t.id === smartRankingCurrentPair.taskB);
                  return task
                    ? {
                        id: task.id,
                        title: task.title,
                        categoryName:
                          categories.find(c => c.id === task.categoryId)?.name ||
                          'Uncategorized',
                        createdAt: task.createdAt,
                      }
                    : null;
                })()
              : null
          }
          mergeSortCurrentComparison={
            smartRankingState
              ? smartRankingState.comparisonsMade + 1
              : 0
          }
          mergeSortEstimatedTotal={
            smartRankingState && preferenceGraph
              ? (() => {
                  const progress = getSmartRankingProgress(smartRankingState, preferenceGraph);
                  return progress.estimatedTotal;
                })()
              : smartRankingState?.estimatedTotal || 0
          }
        />

        {/* Bucketing Modal */}
        {rerankState?.bucketingState && (
          <BucketingModal
            opened={isBucketingModalOpen}
            onClose={() => {
              setIsBucketingModalOpen(false);
            }}
            state={rerankState.bucketingState}
            tasks={getTasksForBucketing()}
            onStateChange={handleBucketingStateChange}
            onComplete={handleBucketingComplete}
          />
        )}

        {/* Tournament Modal */}
        {rerankState?.tournamentState && (
          <TournamentModal
            opened={isTournamentModalOpen}
            onClose={() => {
              setIsTournamentModalOpen(false);
            }}
            state={rerankState.tournamentState}
            tasks={getTasksForTournament()}
            onStateChange={handleTournamentStateChange}
            onComplete={handleTournamentComplete}
            onMarkTaskDone={handleMarkTaskDoneInTournament}
          />
        )}
      </div>
    </MantineProvider>
  );
}

export default App;
